import React, { useState, useEffect, useMemo, useCallback, forwardRef, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, TimeScale, ChartData, ChartOptions, TooltipModel,ChartType // ADDED BarElement
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { Bar } from 'react-chartjs-2';
import { format, subDays, startOfDay, endOfDay, differenceInDays, addDays, parse, eachDayOfInterval } from 'date-fns'; // Added parse, eachDayOfInterval
import DatePicker from 'react-datepicker';
import { enIN } from 'date-fns/locale';
// Assuming queryGraphql is a local helper. If it's from a library, adjust path.
import queryGraphql from "../helpers/ApiInterface"; // Path to your API interface
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import InputAdornment from '@mui/material/InputAdornment';
import Container from '@mui/material/Container';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import Chip from '@mui/material/Chip';
import Popper from '@mui/material/Popper'; // Import Popper
import Fade from '@mui/material/Fade';     // Import Fade

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { RiArchiveDrawerLine } from "react-icons/ri";
import { useTheme, useMediaQuery, styled} from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
// Assuming MiniBot is a local component. If it's from a library, adjust path.
import MiniBot from './MiniBot'; // Path to your MiniBot component
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "./MainDrawer"; // Adjust path if needed

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, TimeScale, Title, Tooltip, Legend); // ADDED BarElement
// Add this right after your import statements in CostDashboard.tsx

// ===================================================================================
// --- NEW: MANUAL MOCK DATA GENERATION LOGIC (COPY-PASTE THIS ENTIRE BLOCK) ---
// ===================================================================================

const MOCK_COST_PERIOD_DAYS = 30; // The mock data arrays are 30 days long

/**
 * Slices a 30-day manual cost array to fit the user's selected date range.
 * The last value in the cost array corresponds to "yesterday".
 */
const getMockCostDataForRange = (startDate: Date, endDate: Date, manualCostArray: number[]): CostDataPoint[] => {
    const requestedDays = eachDayOfInterval({ start: startDate, end: endDate });
    const today = startOfDay(new Date());

    return requestedDays.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const daysAgo = differenceInDays(today, day);
        const index = MOCK_COST_PERIOD_DAYS - daysAgo;

        // Use the cost if the date falls within our 30-day mock period, otherwise default to 0
        const cost = (index >= 0 && index < manualCostArray.length) ? manualCostArray[index] : 0;

        return { date: dateStr, cost };
    });
};

/**
 * Generates mock data for K8s Clusters from manual cost arrays.
 */
const generateMockClusterData = (startDate: Date, endDate: Date): ApiClusterResponse => {
    // --- EDIT a 30-day cost array for each cluster. The last number is yesterday's cost. ---
    const MOCK_CLUSTERS = [
        { name: 'futurify', costs: [919, 864, 853, 944, 885, 881, 878, 867, 944, 863, 936, 944, 919, 861, 925, 904, 854, 853, 861, 877, 879, 914, 927, 853, 921, 875, 941, 933, 939, 931] },
        { name: 'axios', costs: [842, 758, 859, 873, 897, 753, 802, 868, 875, 821, 791, 758, 883, 875, 833, 769, 813, 842, 761, 857, 785, 840, 847, 857, 822, 817, 866, 794, 827, 896] },
        // { name: 'eks-staging-eu-west1', costs: [120, 122, 125, 123, 121, 124, 126, 128, 130, 129, 127, 125, 122, 120, 118, 119, 121, 124, 127, 130, 132, 135, 133, 131, 130, 135, 138, 140, 142, 145] },
        // { name: 'gke-analytics-us-east1', costs: [310, 300, 290, 295, 305, 315, 320, 325, 330, 335, 340, 330, 320, 310, 300, 305, 315, 325, 335, 345, 350, 360, 370, 380, 390, 400, 395, 390, 410, 420] },
    ];

    return MOCK_CLUSTERS.map(cluster => ({
        clusterName: cluster.name,
        data: getMockCostDataForRange(startDate, endDate, cluster.costs),
    }));
};

/**
 * Generates mock data for K8s Applications from manual cost arrays.
 */
const generateMockApplicationData = (startDate: Date, endDate: Date): FetchedApplicationData[] => {
    // --- EDIT a 30-day cost array for each application. ---
   const MOCK_APPS = [
    {
        id: 'app1', 
        name: 'cortex', 
        clusterName: 'futurify', 
        costs: [342, 328, 325, 348, 333, 332, 332, 329, 348, 328, 
              346, 348, 342, 327, 343, 338, 326, 325, 327, 331, 
              332, 341, 344, 325, 342, 331, 347, 345, 347, 345] 
    },
    {
        id: 'app2', 
        name: 'nexus', 
        clusterName: 'futurify', 
        costs: [338, 332, 339, 343, 333, 350, 325, 349, 350, 330, 
              347, 338, 335, 333, 329, 331, 349, 335, 328, 327, 
              337, 328, 336, 336, 344, 333, 350, 326, 348, 339] 
    },
    {
        id: 'app3', 
        name: 'vertex', 
        clusterName: 'axios', 
        costs: [331, 328, 337, 327, 342, 334, 345, 344, 336, 343, 
              331, 347, 327, 326, 346, 332, 349, 334, 327, 332, 
              328, 337, 333, 339, 345, 336, 330, 336, 336, 342] 
    },
    {
        id: 'app4', 
        name: 'cipher', 
        clusterName: 'axios', 
        costs: [346, 333, 347, 346, 345, 327, 344, 345, 330, 342, 
              348, 332, 330, 339, 337, 333, 345, 347, 342, 332, 
              346, 335, 349, 349, 326, 332, 326, 350, 335, 337] 
    },
];

    return MOCK_APPS.map(app => {
        const dailyCosts = getMockCostDataForRange(startDate, endDate, app.costs);
        const totalCostForLabel = dailyCosts.reduce((sum, item) => sum + item.cost, 0);
        // Use first and last day for a consistent change calculation
        const costDifferenceForLabel = dailyCosts.length > 1 ? (dailyCosts[dailyCosts.length - 1].cost - dailyCosts[0].cost) : 0;

        return {
            id: app.id,
            applicationName: app.name,
            clusterInfo: { id: app.clusterName, clusterName: app.clusterName },
            dailyCosts,
            totalCostForLabel,
            costDifferenceForLabel,
        };
    });
};

/**
 * Generates mock data for K8s Services from manual cost arrays.
 */
const generateMockServiceData = (startDate: Date, endDate: Date): FetchedServiceData[] => {
    // --- EDIT a 30-day cost array for each service. ---
    const MOCK_SERVICES = [
    {
        id: 'svc1', 
        name: 'axiosservice', 
        appName: 'vertex', 
        clusterName: 'axios', 
        costs: [84, 81, 84, 82, 83, 80, 85, 84, 81, 84, 
              81, 82, 83, 85, 81, 82, 83, 80, 82, 81, 
              81, 80, 83, 84, 82, 81, 83, 82, 81, 85] 
    },
    {
        id: 'svc2', 
        name: 'data-integration', 
        appName: 'cipher', 
        clusterName: 'axios', 
        costs: [83, 83, 81, 80, 83, 83, 82, 81, 80, 85, 
              84, 85, 82, 85, 82, 83, 83, 81, 83, 80, 
              83, 85, 85, 83, 80, 83, 81, 84, 80, 82] 
    },
    {
        id: 'svc3', 
        name: 'cloud-migration', 
        appName: 'cipher', 
        clusterName: 'axios', 
        costs: [80, 82, 85, 82, 80, 82, 84, 81, 80, 83, 
              81, 82, 80, 81, 83, 85, 85, 82, 85, 84, 
              83, 81, 83, 84, 82, 81, 85, 84, 85, 86] 
    }
];

    return MOCK_SERVICES.map(svc => {
        const dailyCosts = getMockCostDataForRange(startDate, endDate, svc.costs);
        const totalCostForLabel = dailyCosts.reduce((sum, item) => sum + item.cost, 0);
        const costDifferenceForLabel = dailyCosts.length > 1 ? (dailyCosts[dailyCosts.length - 1].cost - dailyCosts[0].cost) : 0;

        return {
            id: svc.id,
            serviceName: svc.name,
            applicationInfo: {
                id: svc.appName,
                applicationName: svc.appName,
                clusterId: svc.clusterName,
                clusterName: svc.clusterName,
            },
            dailyCosts,
            totalCostForLabel,
            costDifferenceForLabel,
        };
    });
};

/**
 * Generates mock data for Add-ons from manual cost arrays.
 */
const generateMockAddonData = (startDate: Date, endDate: Date): FetchedAddonData[] => {
    // --- EDIT a 30-day cost array for each add-on. ---
    const MOCK_ADDONS = [
    {
        id: 'addon1', 
        description: 'Cloud Storage', 
        appName: 'cortex', 
        clusterName: 'futurify', 
        costs: [62, 66, 61, 68, 63, 60, 71, 71, 60, 61, 
              62, 68, 73, 72, 71, 64, 64, 68, 68, 68, 
              71, 60, 73, 70, 60, 64, 65, 60, 64, 61] 
    },
    {
        id: 'addon2', 
        description: 'Compute Engine', 
        appName: 'nexus', 
        clusterName: 'futurify', 
        costs: [60, 64, 66, 67, 67, 66, 63, 61, 60, 66, 
              65, 65, 65, 60, 60, 67, 66, 63, 69, 73, 
              63, 67, 72, 64, 64, 71, 62, 63, 72, 70] 
    }
];

    return MOCK_ADDONS.map(addon => ({
        id: addon.id,
        serviceDescription: addon.description,
        applicationName: addon.appName,
        clusterName: addon.clusterName,
        dailyCosts: getMockCostDataForRange(startDate, endDate, addon.costs),
    }));
};

// ===================================================================================
// --- END OF MOCK DATA GENERATION BLOCK ---
// ===================================================================================



const StyledDatePickerWrapper = styled(Box)(({ theme }) => ({
    '& .react-datepicker-popper': {
        zIndex: 1300,
    },
    '& .react-datepicker': {
        fontFamily: '"Gilroy-Regular", sans-serif',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '12px',
        backgroundColor: '#fff',
    },

    // The entire default header is now replaced by our custom one,
    // so we only need to style the container for the day names.
    '& .react-datepicker__header': {
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        padding: '0',
    },
    
    '& .react-datepicker__day-names': {
        paddingBlock: '8px',
    },

    '& .react-datepicker__day-name': {
        fontFamily: 'Gilroy-SemiBold',
        color: '#333333',
        margin: '0.25rem',
        fontSize: '0.75rem',
        width : '2rem',
        textAlign: 'center'
    },

    // Day styling (to ensure circles)
    '& .react-datepicker__day': {
        width: '2rem',
        height: '2rem',
        lineHeight: '2rem',
        margin: '0.25rem',
        borderRadius: '50%',
        color: '#212121',
        border: '1px solid transparent',
        //transition: 'all 0.2s ease-in-out', // Re-enabled for smoothness
    },

    // Hover effect (border, not background)
    '& .react-datepicker__day:hover': {
        backgroundColor: 'transparent',
        borderColor: '#212121',
        borderRadius: '50%',
    },
    
    // Grayed out days from other months
    '& .react-datepicker__day--outside-month': {
        color: '#bdbdbd',
        pointerEvents: 'none',
    },

    // Days within the selected range (light gray background)
    '& .react-datepicker__day--in-range': {
        backgroundColor: '#f5f5f5',
        borderRadius: '0', // CORRECTED: Must be 0 for the rectangular background
    },
    
    // Selected Start & End dates (the black circles)
    '& .react-datepicker__day--selected, & .react-datepicker__day--range-start, & .react-datepicker__day--range-end': {
        backgroundColor: '#000',
        color: '#fff',
        borderColor: '#000',
        position: 'relative',
        zIndex: 1,
        '&:hover': {
            backgroundColor: '#333',
            borderColor: '#333',
        }
    },

    // When actively selecting the end date
     '& .react-datepicker__day--in-selecting-range': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        borderRadius: '50%',
    },
}));

// Interface for date range selection
interface DateRange { startDate: Date | null; endDate: Date | null; }
// Interface for a single cost data point (date in 'yyyy-MM-dd' format)
interface CostDataPoint { date: string; cost: number; }
// Interface for cluster cost data, containing cluster name and an array of cost data points
interface ClusterCostData { clusterName: string; data: CostDataPoint[]; }
// Type alias for the API response structure for cluster costs
type ApiClusterResponse = ClusterCostData[];

// GraphQL response types
interface DailyCostRaw {
  usage_date: string; // Example: "April 29"
  daily_total_cost: number;
}

// Base interface for raw row data from GraphQL
interface BaseRowDataRaw {
  id: string;
  daily_costs_array: DailyCostRaw[];
}

// Interface for raw cluster data row from GraphQL, extending BaseRowDataRaw
interface ClusterRowDataRaw extends BaseRowDataRaw {
  clusterName?: string; // Cluster name is optional
}

// Interface for raw application data row from GraphQL, extending BaseRowDataRaw
interface ApplicationRowDataRaw extends BaseRowDataRaw {
  applicationName?: string; // Application name is optional
  environment?: string;
  status?: string;
  applicationOwnerId?: string;
  cluster?: { // Associated cluster information
    id: string;
    clusterName: string;
  };
  total_cost_for_label?: number; // ADDED
  cost_difference_for_label?: number; // ADDED
}

// Interface for raw service data row from GraphQL, extending BaseRowDataRaw
interface ServiceRowDataRaw extends BaseRowDataRaw {
    serviceName?: string;
    // serviceOwnerId?: string; // Optional, based on your example
    application?: { // Associated application information
      id: string;
      applicationName: string;
      cluster?: { // Associated cluster information
        id: string;
        clusterName: string;
      };
    };
    total_cost_for_label?: number; // ADDED
    cost_difference_for_label?: number; // ADDED
  }

  interface AddonRowDataRaw extends BaseRowDataRaw {
    service_description?: string;
    applicationName?: string; // From your GraphQL output example
    cluster?: {             // From your GraphQL output example
      id: string;
      clusterName: string;
    };
    // total_cost_for_label, cost_difference_for_label can be added if needed elsewhere
  }

// Union type for any raw row data, can be cluster, application, or service
type AnyRowDataRaw = ClusterRowDataRaw | ApplicationRowDataRaw | ServiceRowDataRaw | AddonRowDataRaw; // ADDED AddonRowDataRaw


// Interface for the main data structure within the GraphQL response
interface GetCostDashboardDataRaw {
  schemaFields: string[];
  rowsData: AnyRowDataRaw[]; // Array of cluster or application raw data
  totalRows: number;
  total_cost?: number;
  cost_difference?: number;
}

// Interface for the overall GraphQL API response
interface GraphQLApiResponse {
  getCostDashboardData?: GetCostDashboardDataRaw; // Data is optional
  errors?: Array<{ message: string; [key: string]: any }>; // Errors array is optional
}


// Application specific transformed data structures
// Interface for fetched application data after initial transformation
interface FetchedApplicationData {
  id: string;
  applicationName: string;
  clusterInfo: { id: string; clusterName: string };
  dailyCosts: CostDataPoint[]; // Array of { date: 'yyyy-MM-dd', cost: number }
  totalCostForLabel?: number; // ADDED
  costDifferenceForLabel?: number; // ADDED
}
// Interface for fetched service data after initial transformation
interface FetchedServiceData {
    id: string;
    serviceName: string;
    applicationInfo: {
      id: string;
      applicationName: string;
      clusterId: string;
      clusterName: string;
    };
    dailyCosts: CostDataPoint[]; // Array of { date: 'yyyy-MM-dd', cost: number }
    totalCostForLabel?: number; // ADDED
    costDifferenceForLabel?: number; // ADDED
  }

  interface FetchedAddonData {
    id: string;
    serviceDescription: string;
    applicationName: string;
    clusterName: string;
    dailyCosts: CostDataPoint[];
  }
// Interface for a processed application item, ready for display
interface ProcessedApplicationItem {
  appName: string;
  totalCost: number;
  costChange: number; // Change in cost compared to a previous period or trend
  costTrendData: number[]; // Array of costs for the trend visualization
  maxCostInTrend: number; // Maximum cost in the trend data, for scaling visualizations
}
// Interface for a processed service item, ready for display
interface ProcessedServiceItem {
    serviceName: string;
    totalCost: number;
    costChange: number; // Change in cost compared to a previous period or trend
    costTrendData: number[]; // Array of costs for the trend visualization
    maxCostInTrend: number; // Maximum cost in the trend data, for scaling visualizations
  }
// Interface for a processed cluster containing its applications
interface ProcessedClusterWithApps {
  clusterName: string;
  applications: ProcessedApplicationItem[];
}


// --- NEW --- Interface for a processed Non-K8s item (AI or Data Solution)
interface ProcessedNonK8sItem {
    name: string;
    totalCost: number;
    costChange: number;
    costTrendData: number[];
    maxCostInTrend: number;
}

// Interface for the content of the bar chart tooltip (used with Popper)
interface BarTooltipContent {
    date: string; // Formatted date string (e.g., "May 09")
    cost: string; // Formatted cost string (e.g., "$123.45")
}

// Interface for the state of the bar chart tooltip (used with Popper)
interface BarTooltipState {
  visible: boolean;
  content: BarTooltipContent | null; // Structured content for the Popper tooltip, or null if not visible
  anchorElement: HTMLDivElement | null; // The HTML element the tooltip is anchored to
}
// Type for grouped service data: ClusterName -> ApplicationName -> ProcessedServiceItem[]
type GroupedServiceData = Record<string, Record<string, ProcessedServiceItem[]>>;
/**
 * Generates an array of color strings.
 * @param count - The number of colors to generate.
 * @returns An array of hex color strings.
 */
const generateColors = (count: number): string[] => {
// Predefined array of colors for clusters/applications
const baseColors = [ '#6495ED', '#FFB74D', '#4DD0E1', '#81C784', '#BA68C8', '#FF8A65', '#7986CB', '#A1887F', '#90CAF9', '#FFCC80', '#80DEEA', '#A5D6A7' ];
const result: string[] = [];
for (let i = 0; i < count; i++) {
    // Cycle through baseColors if count exceeds its length
    result.push(baseColors[i % baseColors.length]);
}
return result;
};

/**
 * Formats a number as currency (USD) without decimal places for general display.
 * @param value - The number to format.
 * @returns A string representing the formatted currency.
 */
const formatCurrency = (value: number): string => {
return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

/**
 * Formats a number as currency (USD), showing decimals for small values, and handling < > symbols.
 * Used for general UI elements like chart axes or line chart tooltips.
 * @param value - The number to format.
 * @returns A string representing the formatted currency.
 */
const formatCurrencyWithSmallValues = (value: number): string => {
    if (value === 0) return '₹0';
    // For very small values (absolute value less than 0.01 but not zero)
    if (Math.abs(value) < 0.01 && Math.abs(value) > 0) return value > 0 ? '< ₹0.01' : '> -₹0.01';
    // For small values (absolute value less than 1 but not covered above)
    if (Math.abs(value) < 1) return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    // For values greater than or equal to 1 (absolute)
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

/**
 * Formats a number as currency specifically for the Popper tooltip, ensuring "exact" display for small values.
 * @param value - The number to format.
 * @returns A string representing the formatted currency for the tooltip.
 */
const formatCurrencyForPopperTooltip = (value: number): string => {
    if (value === 0) return '₹0.00'; // Display $0.00 for zero

    const absValue = Math.abs(value);

    if (absValue < 0.01) { // For values like 0.003, 0.0001. These need special handling for "exactness".
        let numDecimalPlaces = 2; // Default to 2 decimal places
        if (absValue !== 0) {
            const valueStr = absValue.toString();
            if (valueStr.includes('.')) {
                const decimalPart = valueStr.split('.')[1];
                let lastNonZeroIndex = -1;
                for (let i = decimalPart.length - 1; i >= 0; i--) {
                    if (decimalPart[i] !== '0') {
                        lastNonZeroIndex = i;
                        break;
                    }
                }
                // Number of decimal places needed to show the significant part of the number
                numDecimalPlaces = Math.max(2, lastNonZeroIndex + 1);
            }
        }
        // Cap the number of decimal places to a reasonable limit (e.g., 6 for micro-cents)
        const maxFractionDigitsToShow = Math.min(numDecimalPlaces, 6);

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: maxFractionDigitsToShow,
            maximumFractionDigits: maxFractionDigitsToShow,
        }).format(value);
    }
    if (absValue < 1) { // For values like $0.50, $0.12
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    }
    // For values $1 or greater
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};


/**
 * Formats a currency change value.
 * @param value - The currency change amount.
 * @returns A string representing the formatted change.
 * Shows '0.00' if absolute change is less than 0.01.
 * Shows 2 decimal places if absolute change is less than 1.
 * Shows 0 decimal places if absolute change is 1 or more.
 */
const formatCurrencyChange = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue < 0.01) return '0.00'; // Very small change, display as 0.00
  if (absValue < 1) return absValue.toFixed(2); // Small change, display with 2 decimals
  // Larger change, display with no decimals
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(absValue);
};

// Base style for date filter buttons
const dateFilterBaseStyle = { fontFamily: "Gilroy-Regular", fontSize: "14px", textTransform: "none", borderRadius: 0, px: 2.5, minHeight: '38px', minWidth: 'auto', border: 'none', lineHeight: 'normal', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }, } as const;
// Style for active date filter buttons
const dateFilterActiveStyle = { ...dateFilterBaseStyle, backgroundColor: "black", color: "white", '&:hover': { backgroundColor: "dimgray" }, } as const;
// Style for inactive date filter buttons
const dateFilterInactiveStyle = { ...dateFilterBaseStyle, backgroundColor: "transparent", color: "grey.700", } as const;
// Style for general contained (filled) buttons
const generalContainedButtonStyle = { fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, height: '36.5px', boxSizing: 'border-box', backgroundColor: "black", color: "white", '&:hover': { backgroundColor: "dimgray" }, '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' }, } as const;
// Style for general outlined buttons
const generalOutlinedButtonStyle = { fontFamily: "Gilroy-Bold", fontSize: "14px", borderRadius: "4px", textTransform: "none", px: 3, py: 1, height: '36.5px', boxSizing: 'border-box', backgroundColor: "transparent", color: "black", border: '1px solid black', '&:hover': { backgroundColor: "rgba(0, 0, 0, 0.04)", borderColor: 'dimgray', }, '&:disabled': { backgroundColor: 'transparent', borderColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' }, } as const;
// Style for the active toggle button
const toggleActiveStyle = {
    fontFamily: "Gilroy-Medium",
    fontWeight:400,
    fontSize: "14px",
    textTransform: "none",
    borderRadius: '4px', // Rounded corners for the active "pill"
    color: "#333333",
    backgroundColor: "#FFFFFF", // A modern blue for the active state
    px: 2,
    '&:hover': {
        backgroundColor: 'none'// A darker blue for hover
    }
} as const;

// Style for the inactive toggle button
const toggleInactiveStyle = {
    fontFamily: "Gilroy-Regular",
    fontWeight:400,
    fontSize: "14px",
    textTransform: "none",
    color: "#666666",
    backgroundColor: "transparent",
    px: 2,
    '&:hover': {
        backgroundColor: "rgba(0, 0, 0, 0.04)"
    }
} as const;
// Props for the custom date picker input component
interface CustomDatePickerInputProps { value?: string; onClick?: () => void; isActive: boolean; }
// Custom input component for the DatePicker, styled as a button
const CustomDatePickerInput = forwardRef<HTMLButtonElement, CustomDatePickerInputProps>( ({ value, onClick, isActive }, ref) => (<Button ref={ref} onClick={onClick} sx={isActive ? dateFilterActiveStyle : dateFilterInactiveStyle} variant="text"> {isActive && value ? value : "Custom Date"} </Button> ));
CustomDatePickerInput.displayName = 'CustomDatePickerInput'; // For better debugging

// Context for the custom HTML tooltip for the line chart
interface GenericChartTooltipContext {
    chart: ChartJS; // ChartJS is already Chart<ChartType, ...>, so it's generic
    tooltip: TooltipModel<ChartType>; // Use the generic ChartType
  }
/**
 * Custom HTML tooltip renderer for the line chart.
 * @param chart - The Chart.js chart instance.
 * @param tooltip - The tooltip model from Chart.js.
 */
const CustomHtmlLineTooltip = ({ chart, tooltip }: GenericChartTooltipContext): void => {
// Find or create the tooltip HTML element
let tooltipEl = chart.canvas.parentNode?.querySelector<HTMLDivElement>(`div.chartjs-custom-tooltip-${chart.id}`); // MODIFIED: Use chart.id
if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = `chartjs-custom-tooltip-${chart.id}`;
    // Basic styling for the tooltip container
    tooltipEl.style.background = 'rgba(255, 255, 255, 0.97)';
    tooltipEl.style.borderRadius = '6px';
    tooltipEl.style.color = 'black';
    tooltipEl.style.opacity = '0'; // Initially hidden
    tooltipEl.style.pointerEvents = 'none'; // Allow clicks to pass through
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transition = 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease';
    tooltipEl.style.boxShadow = '0px 3px 12px rgba(0, 0, 0, 0.18)';
    tooltipEl.style.border = '1px solid #e0e0e0';
    tooltipEl.style.minWidth = '180px';
    tooltipEl.style.zIndex = '110'; // Ensure it's above chart elements

    const rootDiv = document.createElement('div'); // Content will be rendered here by React
    tooltipEl.appendChild(rootDiv);
    chart.canvas.parentNode?.appendChild(tooltipEl);
}

// Hide tooltip if opacity is 0
if (tooltip.opacity === 0) {
    if (tooltipEl.style.opacity !== '0') {
        tooltipEl.style.opacity = '0';
        const contentRoot = tooltipEl.querySelector('div');
        // Unmount React component when hiding
        if (contentRoot && contentRoot.hasChildNodes()) {
            ReactDOM.unmountComponentAtNode(contentRoot);
        }
    }
    return;
}

// Extract data from the tooltip model
const titleLines = tooltip.title || [];
const dataPoints = tooltip.dataPoints || [];
let totalCost = 0;
dataPoints.forEach(dp => { totalCost += dp.parsed.y || 0; }); // Sum costs from all datasets at this point

// Determine the date title for the tooltip
let dateTitle = '';
// titleLines is tooltip.title, dataPoints is tooltip.dataPoints

if (titleLines.length > 0) {
    const rawDateStr = titleLines[0]; // Chart.js should provide the axis label string here.
                                          // For a time scale, this is typically the date.
    try {
        // new Date() can parse many common date string formats (e.g., "May 14, 2025", "2025-05-14")
        const dateObj = new Date(rawDateStr);
        if (!isNaN(dateObj.getTime())) {
            dateTitle = format(dateObj, 'PPP'); // Consistently format to 'PPP'
        } else {
            // If new Date() fails, it might be a format parse() understands or just use raw.
            // Try parsing with the specific format if you suspect it might be, e.g., 'yyyy-MM-dd'
            // For now, we'll stick to a simpler fallback for this case.
            dateTitle = rawDateStr; // Fallback to the raw string from titleLines
        }
    } catch (e) {
        console.error("Error parsing date from tooltip.title:", rawDateStr, e);
        dateTitle = rawDateStr; // Fallback to the raw string on error
    }
}

// If dateTitle is still not set (e.g., titleLines was empty),
// try using dataPoints[0].parsed.x as a fallback.
// Check for null or undefined explicitly for the timestamp.
if (!dateTitle && dataPoints.length > 0 && dataPoints[0].parsed?.x !== undefined && dataPoints[0].parsed?.x !== null) {
    const timestamp = dataPoints[0].parsed.x;
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
        dateTitle = format(date, 'PPP');
    } else {
        console.warn("Timestamp from dataPoints[0].parsed.x was invalid:", timestamp);
        dateTitle = "Invalid Date"; // Indicate that the timestamp was problematic
    }
}

// Final fallback if no date could be determined
if (!dateTitle) {
    dateTitle = "Date N/A";
}

// Prepare body items for each dataset
const bodyItems = dataPoints.map(dp => {
    const borderColor = dp.dataset?.borderColor as string || '#000';
    const label = dp.dataset?.label || '';
    const value = dp.parsed?.y;
    const formattedValue = typeof value === 'number' ? formatCurrencyWithSmallValues(value) : '';
    return { label, formattedValue, color: borderColor };
});

const contentRoot = tooltipEl.querySelector('div'); // The div where React content is rendered
if (!contentRoot) return;

// Render tooltip content using React
ReactDOM.render(
    <Box sx={{ p: 1.5, fontFamily: 'Gilroy-Regular', maxWidth: '300px' }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', textAlign: 'left', fontFamily: 'Gilroy-Regular' }}>
            {dateTitle}
        </Typography>
        <Divider sx={{ mb: 1 }}/>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 1, color: 'black', fontSize: '20px' }} >
                <RiArchiveDrawerLine /> {/* Icon */}
            </Box>
            <Box>
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.2, color: 'text.secondary', fontFamily:'Gilroy-Regular' }}>
                    Overall Cost on this Day
                </Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.3, fontFamily:'Gilroy-SemiBold' }}>
                    {formatCurrencyWithSmallValues(totalCost)}
                </Typography>
            </Box>
        </Box>
        <Divider sx={{ mb: 1 }}/>
        <Stack spacing={0.7}>
            {/* Sort items by cost descending */}
            {bodyItems.sort((a,b) => (Number(b.formattedValue.replace(/[^0-9.-]+/g,"")) || 0) - (Number(a.formattedValue.replace(/[^0-9.-]+/g,"")) || 0)).map((item, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}> {/* For text overflow */}
                        <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: '50%', mr: 1, flexShrink: 0 }}/>
                        <Typography variant="body2" sx={{ mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily:'Gilroy-Regular' }} title={item.label}>{item.label}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', flexShrink: 0, pl:1, fontFamily:'Gilroy-SemiBold' }}>{item.formattedValue}</Typography>
                </Box>
            ))}
        </Stack>
    </Box>,
    contentRoot
);

// Position the tooltip
const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
const chartArea = chart.chartArea ?? { left: 0, top: 0, right: chart.width, bottom: chart.height };
const { left: chartAreaLeft, top: chartAreaTop, right: chartAreaRight, bottom: chartAreaBottom } = chartArea;
const caretX = tooltip.caretX;
const caretY = tooltip.caretY;
const tooltipWidth = tooltipEl.offsetWidth;
const tooltipHeight = tooltipEl.offsetHeight;
const tooltipPadding = 10; // Padding from chart edges and caret

// Calculate final tooltip position, ensuring it stays within chart bounds
let finalLeft = positionX + caretX + tooltipPadding;
let finalTop = positionY + caretY - (tooltipHeight / 2); // Center vertically by default

// Adjust if tooltip goes out of right bound
if (finalLeft + tooltipWidth + tooltipPadding > chartAreaRight) {
    finalLeft = positionX + caretX - tooltipWidth - tooltipPadding; // Place to the left of caret
}
// Adjust if tooltip goes out of left bound
if (finalLeft < chartAreaLeft + tooltipPadding) {
    finalLeft = chartAreaLeft + tooltipPadding;
}
// Adjust if tooltip goes out of top bound
if (finalTop < chartAreaTop + tooltipPadding) {
    finalTop = chartAreaTop + tooltipPadding;
}
// Adjust if tooltip goes out of bottom bound
if (finalTop + tooltipHeight + tooltipPadding > chartAreaBottom) {
    finalTop = chartAreaBottom - tooltipHeight - tooltipPadding;
}

// Apply final position and opacity
tooltipEl.style.opacity = '1';
tooltipEl.style.left = finalLeft + 'px';
tooltipEl.style.top = finalTop + 'px';
tooltipEl.style.transform = ''; // Clear any previous transform
};

const GradientText = styled(Typography)(({ theme }) => ({
    background: `linear-gradient(90deg, #FF40E2, #0064FF66,#0064FF)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    fontWeight: 800,
    display: 'inline-block',
    fontSize: '22px',
    marginRight:'2vw'
  }));
 
// --- NEW --- Generic component for rendering cost list items (for Apps, AI, and Data solutions)
interface CostListItemProps {
    name: string;
    totalCost: number;
    costChange: number;
    costTrendData: number[];
    maxCostInTrend: number;
    dateRange: DateRange;
    onBarMouseEnter: (event: React.MouseEvent<HTMLDivElement>, dateStr: string, cost: number) => void;
    onBarMouseLeave: () => void;
}

const CostListItem: React.FC<CostListItemProps> = ({
    name, totalCost, costChange, costTrendData, maxCostInTrend, dateRange, onBarMouseEnter, onBarMouseLeave
}) => {
    return (
        <Box
            key={name}
            sx={{
                display: 'flex', alignItems: 'center', py: 1.5, pr: { xs: 1, sm: 2 }, pl: { xs: 1, sm: 2 },
                borderBottom: 1, borderColor: 'grey.200',
                '&:last-child': { borderBottom: 0 },
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' }
            }}
        >
            <Typography variant="body2" title={name} sx={{ flex: { xs: '1 1 40%', sm: '0 0 40%' }, fontFamily: 'Gilroy-Regular', pr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name}
            </Typography>
            <Box sx={{ flex: { xs: '1 1 30%', sm: '0 0 25%' }, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'Gilroy-SemiBold', textAlign: 'right', mr: 0.5 }}>
                    {formatCurrencyWithSmallValues(totalCost)}
                </Typography>
                <Chip
                    icon={
                        costChange === 0 ? <Typography fontFamily="Gilroy-Medium" fontSize="12px" sx={{ display: 'flex', alignItems: 'center', color: "text.secondary" }}>–</Typography>
                        : costChange > 0 ?
                        <Typography fontFamily="Gilroy-Medium" fontSize="12px" sx={{ display: 'flex', alignItems: 'center', color: "#F04438" }}> <img src={"/assets/arrow-up-red.svg"} alt="Up" style={{ width: '12px', height: '12px' }} /></Typography>
                        : <Typography fontFamily="Gilroy-Medium" fontSize="12px" sx={{ display: 'flex', alignItems: 'center', color: "#12B76A" }}> <img src={"/assets/arrow-down-green.svg"} alt="Down" style={{ width: '12px', height: '12px' }} /></Typography>
                    }
                    label={`₹${formatCurrencyChange(costChange)}`}
                    size="small"
                    sx={{
                        height: '18px', fontSize: '0.65rem', fontFamily: 'Gilroy-Medium',
                        backgroundColor: costChange === 0 ? 'grey.200' : costChange > 0 ? '#FEE4E2' : '#D1FADF',
                        color: costChange === 0 ? 'text.primary' : costChange > 0 ? '#D92D20' : '#039855',
                        '.MuiChip-label': { px: '4px' },
                        '.MuiChip-icon': { ml: costChange === 0 ? '0px' : '3px', mr: costChange === 0 ? '0px' :'-3px', color: 'inherit' }
                    }}
                />
            </Box>
            <Box sx={{ flex: { xs: '1 1 30%', sm: '0 0 35%' }, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '24px', minWidth: '100px' }}>
                {costTrendData.map((val: number, idx: number) => {
                    const barDateStr = format(addDays(dateRange.startDate!, idx), 'yyyy-MM-dd');
                    const barHeight = maxCostInTrend > 0 ? Math.max(1, (val / maxCostInTrend) * 100) : 1;
                    return (
                        <Box
                            key={idx}
                            onMouseEnter={(e) => onBarMouseEnter(e, barDateStr, val)}
                            onMouseLeave={onBarMouseLeave}
                            sx={{
                                width: `${Math.max(2, 100 / (costTrendData.length || 1) - 1)}%`,
                                maxWidth: '6px', minWidth: '2px',
                                height: `${barHeight}%`,
                                backgroundColor: val === 0 ? 'grey.300' : (costChange === 0 ? 'grey.500' : costChange > 0 ? '#F97066' : '#6CE9A6'),
                                mr: '1px', display: 'inline-block',
                                transition: 'background-color 0.2s', cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: val === 0 ? 'grey.400' : (costChange === 0 ? 'grey.700' : costChange > 0 ? '#D92D20' : '#039855')
                                }
                            }} />
                    );
                })}
            </Box>
        </Box>
    );
};

// --- NEW --- Generic component for multiselect dropdown filters
interface SolutionToggleBarProps {
     options: readonly string[];
     activeOption: string;
     onToggle: (option: string) => void;
 }

 const SolutionToggleBar: React.FC<SolutionToggleBarProps> = ({ options, activeOption, onToggle }) => (
     <Box sx={{ display: 'inline-flex', alignItems: 'stretch', p: '4px',height: '38px', bgcolor: '#F5F5F5', borderRadius: '6px', overflow: 'hidden' }}>
         {options.map((option, index) => (
             <React.Fragment key={option}>
                 <Button sx={activeOption === option ? toggleActiveStyle : toggleInactiveStyle} onClick={() => onToggle(option)} variant="text">{option}</Button>
                 {/* {index < options.length - 1 && <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />} */}
             </React.Fragment>
         ))}
     </Box>
 );


const CostDashboardContent: React.FC = () => {
 const USE_MOCK_DATA = true; // <-- MASTER SWITCH: Set to `false` to use real API data
const navigate = useNavigate(); // For navigation
// --- NEW --- State for main resource type toggle
const [resourceType, setResourceType] = useState<'k8s' | 'non-k8s'>('k8s');

// State for active date filter (e.g., 'last30days', 'custom')
const [activeDateFilter, setActiveDateFilter] = useState<string>('last30days');
// State for the selected date range
const [dateRange, setDateRange] = useState<DateRange>(() => {
    // --- CORRECTED LOGIC ---
    // This function sets the initial date range.
    const getInitialDateRange = () => {
        const today = new Date();

        if (USE_MOCK_DATA) {
            // If using mock data, the range should be relative to YESTERDAY.
            // The last 30 days are from 30 days before yesterday to yesterday.
            const yesterday = subDays(today, 1);
            return {
                startDate: startOfDay(subDays(yesterday, 29)),
                endDate: endOfDay(yesterday)
            };
        } else {
            // If using real data, the range can go up to the current moment.
            return {
                startDate: startOfDay(subDays(today, 29)),
                endDate: endOfDay(today)
            };
        }
    };
    return getInitialDateRange();
});
const datePickerRef = useRef<DatePicker>(null);
// Cluster States
const [allClusterData, setAllClusterData] = useState<ApiClusterResponse>([]); // Raw fetched cluster data
const [availableClusters, setAvailableClusters] = useState<string[]>([]); // Names of all available clusters
const [selectedClusterNames, setSelectedClusterNames] = useState<string[]>([]); // Names of currently selected clusters for chart
const [stagedClusterSelection, setStagedClusterSelection] = useState<string[]>([]); // Clusters selected in dropdown before applying
const [loadingClusters, setLoadingClusters] = useState<boolean>(true); // Loading state for cluster data
const [errorClusters, setErrorClusters] = useState<string | null>(null); // Error message for cluster data fetching
const [clusterChartColors, setClusterChartColors] = useState<Record<string, string>>({}); // Map of cluster name to chart color
const selectedClustersSet = useMemo(() => new Set(selectedClusterNames), [selectedClusterNames]); // Memoized Set for efficient lookups
const [isClusterDropdownOpen, setIsClusterDropdownOpen] = useState(false); // State for cluster dropdown visibility
const [clusterSearchTerm, setClusterSearchTerm] = useState(''); // Search term for filtering clusters in dropdown

// Application States
const [allApplicationRawData, setAllApplicationRawData] = useState<FetchedApplicationData[]>([]); // Raw fetched application data
const [availableApplications, setAvailableApplications] = useState<string[]>([]); // Names of applications available based on selected clusters
const [selectedApplicationNames, setSelectedApplicationNames] = useState<string[]>([]); // Names of selected applications for display (empty means 'All')
const [stagedApplicationSelection, setStagedApplicationSelection] = useState<string[]>([]); // Applications selected in dropdown before applying
const [loadingApplications, setLoadingApplications] = useState<boolean>(false); // Loading state for application data
const [errorApplications, setErrorApplications] = useState<string | null>(null); // Error message for application data fetching
const [isApplicationDropdownOpen, setIsApplicationDropdownOpen] = useState(false); // State for application dropdown visibility
const [applicationSearchTerm, setApplicationSearchTerm] = useState(''); // Search term for filtering applications
const [processedApplicationData, setProcessedApplicationData] = useState<ProcessedClusterWithApps[]>([]); // Processed application data grouped by cluster for display
// Initialize barTooltip state for Popper
const [barTooltip, setBarTooltip] = useState<BarTooltipState>({ visible: false, content: null, anchorElement: null });

// Service States
const [allServiceRawData, setAllServiceRawData] = useState<FetchedServiceData[]>([]); // Raw fetched service data
const [availableServices, setAvailableServices] = useState<string[]>([]); // Names of services available based on selected clusters/apps
const [selectedServiceNames, setSelectedServiceNames] = useState<string[]>([]); // Names of selected services for display (empty means 'All')
const [stagedServiceSelection, setStagedServiceSelection] = useState<string[]>([]); // Services selected in dropdown before applying
const [loadingServices, setLoadingServices] = useState<boolean>(false); // Loading state for service data
const [errorServices, setErrorServices] = useState<string | null>(null); // Error message for service data fetching
const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false); // State for service dropdown visibility
const [serviceSearchTerm, setServiceSearchTerm] = useState(''); // Search term for filtering services
// This state will hold the data structured for the UI snippet's rendering logic
const [processedAndGroupedServiceData, setProcessedAndGroupedServiceData] = useState<GroupedServiceData>({});

// Addon Cost States
const [allAddonRawData, setAllAddonRawData] = useState<FetchedAddonData[]>([]);
const [loadingAddonCosts, setLoadingAddonCosts] = useState<boolean>(false); // Initial state for UI
const [errorAddonCosts, setErrorAddonCosts] = useState<string | null>(null);
const [addonChartData, setAddonChartData] = useState<ChartData<'bar'>>({ labels: [], datasets: [] });
// addonChartOptions will be defined using useMemo later

// AI Agent Cost States (NEW)
const [loadingAiAgentCosts, setLoadingAiAgentCosts] = useState<boolean>(false);
const [errorAiAgentCosts, setErrorAiAgentCosts] = useState<string | null>(null);
const [aiAgentChartData, setAiAgentChartData] = useState<ChartData<'bar'>>({ labels: [], datasets: [] });

// Notebooks Cost States (NEW)
const [loadingNotebooksCosts, setLoadingNotebooksCosts] = useState<boolean>(false);
const [errorNotebooksCosts, setErrorNotebooksCosts] = useState<string | null>(null);
const [notebooksChartData, setNotebooksChartData] = useState<ChartData<'bar'>>({ labels: [], datasets: [] });


// ADD THESE NEW STATES for Overall Cluster Cost Summary:
const [overallTotalCostFromApi, setOverallTotalCostFromApi] = useState<number | null>(null);
const [overallCostDifferenceFromApi, setOverallCostDifferenceFromApi] = useState<number | null>(null);

// ADD THESE NEW STATES for Overall Application Cost Summary:
const [overallApplicationTotalCostFromApi, setOverallApplicationTotalCostFromApi] = useState<number | null>(null);
const [overallApplicationCostDifferenceFromApi, setOverallApplicationCostDifferenceFromApi] = useState<number | null>(null);

// ADD THESE NEW STATES for Overall Service Cost Summary:
const [overallServiceTotalCostFromApi, setOverallServiceTotalCostFromApi] = useState<number | null>(null);
const [overallServiceCostDifferenceFromApi, setOverallServiceCostDifferenceFromApi] = useState<number | null>(null);

 // --- NEW --- States for Non-K8s View
type NonK8sCategoryData = Record<string, ProcessedNonK8sItem[]>;

 const [loadingNonK8s, setLoadingNonK8s] = useState(true);
 const [nonK8sChartData, setNonK8sChartData] = useState<ChartData<'line'>>({ labels: [], datasets: [] });

 const AI_SOLUTION_CATEGORIES = ['AI Agent', 'RAG', 'Fine Tuning', 'Notebooks'] as const;
 type AiSolutionCategory = typeof AI_SOLUTION_CATEGORIES[number];
 const [activeAiSolution, setActiveAiSolution] = useState<AiSolutionCategory>('AI Agent');
 const [processedAiSolutionsData, setProcessedAiSolutionsData] = useState<NonK8sCategoryData>({});
 const [overallAiSolutionCost, setOverallAiSolutionCost] = useState({ total: 0, change: 0 });

 const DATA_SOLUTION_CATEGORIES = ['Data Pipelines', 'Data Warehouse', 'Analytics & BI'] as const;
 type DataSolutionCategory = typeof DATA_SOLUTION_CATEGORIES[number];
 const [activeDataSolution, setActiveDataSolution] = useState<DataSolutionCategory>('Data Pipelines');
 const [processedDataSolutionsData, setProcessedDataSolutionsData] = useState<NonK8sCategoryData>({});
 const [overallDataSolutionCost, setOverallDataSolutionCost] = useState({ total: 0, change: 0 });

/**
 * Fetches cluster cost data from the GraphQL API.
 * @param start - The start date of the range.
 * @param end - The end date of the range.
 * @param currentActiveDateFilter - The active date filter type (e.g., 'last30days').
 * @returns A promise that resolves to an array of cluster cost data.
 */
// const fetchClusterCostData = useCallback(
//     async (start: Date, end: Date, currentActiveDateFilter: string): Promise<ApiClusterResponse> => {
//       setLoadingClusters(true);
//       setErrorClusters(null);

//       // Validate date range
//       if (!start || !end || start > end) {
//         console.warn("Invalid date range for fetching cluster data:", start, end);
//         setLoadingClusters(false);
//         return [];
//       }

//       // Determine API parameters based on date filter
//       let inputTimeParam: string;
//       let gqlStartTime: string | undefined = undefined;
//       let gqlEndTime: string | undefined = undefined;

//       switch (currentActiveDateFilter) {
//         case 'last30days': inputTimeParam = '30_days'; break;
//         case 'last7days': inputTimeParam = '7_days'; break;
//         case 'yesterday': inputTimeParam = 'yesterday'; break;
//         case 'custom':
//           inputTimeParam = 'custom';
//           gqlStartTime = format(start, 'yyyy-MM-dd');
//           gqlEndTime = format(end, 'yyyy-MM-dd');
//           break;
//         default:
//           console.error('Unknown activeDateFilter for API:', currentActiveDateFilter);
//           setLoadingClusters(false);
//           setErrorClusters('Invalid date filter configuration.');
//           return [];
//       }

//       // GraphQL query string
//       const gqlQueryString = `
//         query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
//           getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
//         }
//       `;
//       const variables = { resourceType: "cluster", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

//       try {
//         const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
//         // Handle GraphQL errors
//         if (response.errors && response.errors.length > 0) {
//           throw new Error(`GraphQL error: ${response.errors.map(e => e.message).join(', ')}`);
//         }
//         // Handle no data scenario
//         if (!response.getCostDashboardData?.rowsData) { return []; }

//         const apiResult = response.getCostDashboardData;
//         // Transform raw data to the expected format
//         const transformedData: ApiClusterResponse = (apiResult.rowsData as ClusterRowDataRaw[]).map(clusterRaw => {
//             if (apiResult.total_cost !== undefined) {
//                 setOverallTotalCostFromApi(apiResult.total_cost);
//             } else {
//                 setOverallTotalCostFromApi(null); // Reset if not present
//             }
//             if (apiResult.cost_difference !== undefined) {
//                 setOverallCostDifferenceFromApi(apiResult.cost_difference);
//             } else {
//                 setOverallCostDifferenceFromApi(null); // Reset if not present
//             }
//           const clusterName = clusterRaw.clusterName || `Unknown Cluster (${clusterRaw.id})`; // Fallback for missing cluster name
//           const costDataPoints: CostDataPoint[] = (clusterRaw.daily_costs_array || []).map(dailyCost => {
//             let dateStr = '';
//             try {
//               // Parse date string like "April 29" using the query start date for year context
//               const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start);
//               dateStr = format(parsedDate, 'yyyy-MM-dd');
//             } catch (e) {
//               console.error(`Error parsing cluster date string: "${dailyCost.usage_date}" with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
//               dateStr = 'invalid-date'; // Mark as invalid to filter out
//             }
//             return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
//           }).filter(dp => dp.date !== 'invalid-date'); // Remove data points with unparseable dates
//           return { clusterName: clusterName, data: costDataPoints, };
//         }).filter(cluster => cluster.data.length > 0); // Filter out clusters that end up with no valid data points
//         return transformedData;
//       } catch (err) {
//         console.error("Error fetching or transforming cluster costs:", err);
//         setErrorClusters(err instanceof Error ? err.message : 'An unknown error occurred while fetching cluster data');
//         setOverallTotalCostFromApi(null);
//             setOverallCostDifferenceFromApi(null);
//         return []; // Return empty array on error
//       } finally {
//         setLoadingClusters(false);
//       }
//     },
//     [] // No dependencies as all inputs are parameters or defined within
//   );
const fetchClusterCostData = useCallback(
    async (start: Date, end: Date, currentActiveDateFilter: string): Promise<ApiClusterResponse> => {
        setLoadingClusters(true);
        setErrorClusters(null);

        // --- NEW --- Mock data check
        if (USE_MOCK_DATA) {
            return new Promise(resolve => {
                setTimeout(() => {
                    const mockData = generateMockClusterData(start, end);
                    const totalCost = mockData.flatMap(c => c.data).reduce((sum, item) => sum + item.cost, 0);
                    // Simulate overall cost summary fields
                    setOverallTotalCostFromApi(totalCost);
                    const firstDayCost = mockData.flatMap(c => c.data[0]?.cost || 0).reduce((a, b) => a + b, 0);
                    const lastDayCost = mockData.flatMap(c => c.data[c.data.length - 1]?.cost || 0).reduce((a, b) => a + b, 0);
                    setOverallCostDifferenceFromApi(lastDayCost - firstDayCost);
                    setLoadingClusters(false);
                    resolve(mockData);
                }, 300); // Simulate network delay
            });
        }
        // End of mock data block

        // Validate date range
        if (!start || !end || start > end) {
            console.warn("Invalid date range for fetching cluster data:", start, end);
            setLoadingClusters(false);
            return [];
        }

        // Determine API parameters based on date filter
        let inputTimeParam: string;
        let gqlStartTime: string | undefined = undefined;
        let gqlEndTime: string | undefined = undefined;

        switch (currentActiveDateFilter) {
            case 'last30days': inputTimeParam = '30_days'; break;
            case 'last7days': inputTimeParam = '7_days'; break;
            case 'yesterday': inputTimeParam = 'yesterday'; break;
            case 'custom':
                inputTimeParam = 'custom';
                gqlStartTime = format(start, 'yyyy-MM-dd');
                gqlEndTime = format(end, 'yyyy-MM-dd');
                break;
            default:
                console.error('Unknown activeDateFilter for API:', currentActiveDateFilter);
                setLoadingClusters(false);
                setErrorClusters('Invalid date filter configuration.');
                return [];
        }

        // GraphQL query string
        const gqlQueryString = `
        query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
            getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
        }
        `;
        const variables = { resourceType: "cluster", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

        try {
            const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
            // Handle GraphQL errors
            if (response.errors && response.errors.length > 0) {
                throw new Error(`GraphQL error: ${response.errors.map(e => e.message).join(', ')}`);
            }
            // Handle no data scenario
            if (!response.getCostDashboardData?.rowsData) { return []; }

            const apiResult = response.getCostDashboardData;
            // Transform raw data to the expected format
            const transformedData: ApiClusterResponse = (apiResult.rowsData as ClusterRowDataRaw[]).map(clusterRaw => {
                if (apiResult.total_cost !== undefined) {
                    setOverallTotalCostFromApi(apiResult.total_cost);
                } else {
                    setOverallTotalCostFromApi(null); // Reset if not present
                }
                if (apiResult.cost_difference !== undefined) {
                    setOverallCostDifferenceFromApi(apiResult.cost_difference);
                } else {
                    setOverallCostDifferenceFromApi(null); // Reset if not present
                }
                const clusterName = clusterRaw.clusterName || `Unknown Cluster (${clusterRaw.id})`; // Fallback for missing cluster name
                const costDataPoints: CostDataPoint[] = (clusterRaw.daily_costs_array || []).map(dailyCost => {
                    let dateStr = '';
                    try {
                        // Parse date string like "April 29" using the query start date for year context
                        const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start);
                        dateStr = format(parsedDate, 'yyyy-MM-dd');
                    } catch (e) {
                        console.error(`Error parsing cluster date string: "${dailyCost.usage_date}" with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
                        dateStr = 'invalid-date'; // Mark as invalid to filter out
                    }
                    return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
                }).filter(dp => dp.date !== 'invalid-date'); // Remove data points with unparseable dates
                return { clusterName: clusterName, data: costDataPoints, };
            }).filter(cluster => cluster.data.length > 0); // Filter out clusters that end up with no valid data points
            return transformedData;
        } catch (err) {
            console.error("Error fetching or transforming cluster costs:", err);
            setErrorClusters(err instanceof Error ? err.message : 'An unknown error occurred while fetching cluster data');
            setOverallTotalCostFromApi(null);
                setOverallCostDifferenceFromApi(null);
            return []; // Return empty array on error
        } finally {
            setLoadingClusters(false);
        }
    },
    [] // No dependency needed for the USE_MOCK_DATA const
);
/**
 * Fetches application cost data from the GraphQL API.
 * @param start - The start date of the range.
 * @param end - The end date of the range.
 * @param currentActiveDateFilter - The active date filter type.
 * @returns A promise that resolves to an array of fetched application data.
 */
// const fetchApplicationCostData = useCallback(
//     async (start: Date, end: Date, currentActiveDateFilter: string): Promise<FetchedApplicationData[]> => {
//         setLoadingApplications(true);
//         setErrorApplications(null);

//         if (!start || !end || start > end) {
//             console.warn("Invalid date range for fetching application data:", start, end);
//             setLoadingApplications(false);
//             return [];
//         }

//         let inputTimeParam: string;
//         let gqlStartTime: string | undefined = undefined;
//         let gqlEndTime: string | undefined = undefined;

//         switch (currentActiveDateFilter) {
//             case 'last30days': inputTimeParam = '30_days'; break;
//             case 'last7days': inputTimeParam = '7_days'; break;
//             case 'yesterday': inputTimeParam = 'yesterday'; break;
//             case 'custom':
//                 inputTimeParam = 'custom';
//                 gqlStartTime = format(start, 'yyyy-MM-dd');
//                 gqlEndTime = format(end, 'yyyy-MM-dd');
//                 break;
//             default:
//                 console.error('Unknown activeDateFilter for Application API:', currentActiveDateFilter);
//                 setLoadingApplications(false);
//                 setErrorApplications('Invalid date filter configuration for applications.');
//                 return [];
//         }

//         const gqlQueryString = `
//             query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
//               getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
//             }
//           `;
//         const variables = { resourceType: "application", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

//         try {
//             const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
//             if (response.errors && response.errors.length > 0) {
//                 throw new Error(`GraphQL error fetching applications: ${response.errors.map(e => e.message).join(', ')}`);
//             }
//             if (!response.getCostDashboardData?.rowsData) {
//                 return [];
//             }

//             const apiResult = response.getCostDashboardData;
//             if (apiResult.total_cost !== undefined) {
//                 setOverallApplicationTotalCostFromApi(apiResult.total_cost);
//               } else {
//                 setOverallApplicationTotalCostFromApi(null);
//               }
//               if (apiResult.cost_difference !== undefined) {
//                 setOverallApplicationCostDifferenceFromApi(apiResult.cost_difference);
//               } else {
//                 setOverallApplicationCostDifferenceFromApi(null);
//               }
//             const transformedData: FetchedApplicationData[] = (apiResult.rowsData as ApplicationRowDataRaw[]).map(appRaw => {
//                 const appName = appRaw.applicationName || `Unknown App (${appRaw.id})`; // Fallback for app name
//                 // Application must have cluster info to be processed
//                 if (!appRaw.cluster || !appRaw.cluster.clusterName) {
//                     console.warn(`Application with ID ${appRaw.id} (${appName}) is missing cluster information. It will be excluded.`);
//                     return null; // Mark for filtering
//                 }

//                 const costDataPoints: CostDataPoint[] = (appRaw.daily_costs_array || []).map(dailyCost => {
//                     let dateStr = '';
//                     try {
//                         const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start); // Use query start date for year context
//                         dateStr = format(parsedDate, 'yyyy-MM-dd');
//                     } catch (e) {
//                         console.error(`Error parsing application date string: "${dailyCost.usage_date}" for app ${appName} with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
//                         dateStr = 'invalid-date';
//                     }
//                     return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
//                 }).filter(dp => dp.date !== 'invalid-date');

//                 return {
//                     id: appRaw.id,
//                     applicationName: appName,
//                     clusterInfo: { id: appRaw.cluster.id, clusterName: appRaw.cluster.clusterName },
//                     dailyCosts: costDataPoints,
//                     totalCostForLabel: appRaw.total_cost_for_label, // ADDED
//                     costDifferenceForLabel: appRaw.cost_difference_for_label, // ADDED
//                 };
//             }).filter(app => app !== null && app.dailyCosts.length > 0) as FetchedApplicationData[]; // Filter out nulls and apps with no valid cost data
//             return transformedData;

//         } catch (err) {
//             console.error("Error fetching or transforming application costs:", err);
//             setErrorApplications(err instanceof Error ? err.message : 'An unknown error occurred while fetching application data');
//             setOverallApplicationTotalCostFromApi(null); // ADDED
//             setOverallApplicationCostDifferenceFromApi(null); // ADDED
//             return [];
//         } finally {
//             setLoadingApplications(false);
//         }
//     },
//     [] // No dependencies
// );
const fetchApplicationCostData = useCallback(
    async (start: Date, end: Date, currentActiveDateFilter: string): Promise<FetchedApplicationData[]> => {
        setLoadingApplications(true);
        setErrorApplications(null);

        // --- NEW --- Mock data check
        if (USE_MOCK_DATA) {
            return new Promise(resolve => {
                setTimeout(() => {
                    const mockData = generateMockApplicationData(start, end);
                    const totalCost = mockData.reduce((sum, app) => sum + (app.totalCostForLabel || 0), 0);
                    // Simulate overall cost summary fields
                    setOverallApplicationTotalCostFromApi(totalCost);
                    const firstDayCost = mockData.map(d => d.dailyCosts[0]?.cost || 0).reduce((a, b) => a + b, 0);
                    const lastDayCost = mockData.map(d => d.dailyCosts[d.dailyCosts.length - 1]?.cost || 0).reduce((a, b) => a + b, 0);
                    setOverallApplicationCostDifferenceFromApi(lastDayCost - firstDayCost);
                    setLoadingApplications(false);
                    resolve(mockData);
                }, 300); // Simulate network delay
            });
        }
        // End of mock data block

        if (!start || !end || start > end) {
            console.warn("Invalid date range for fetching application data:", start, end);
            setLoadingApplications(false);
            return [];
        }

        let inputTimeParam: string;
        let gqlStartTime: string | undefined = undefined;
        let gqlEndTime: string | undefined = undefined;

        switch (currentActiveDateFilter) {
            case 'last30days': inputTimeParam = '30_days'; break;
            case 'last7days': inputTimeParam = '7_days'; break;
            case 'yesterday': inputTimeParam = 'yesterday'; break;
            case 'custom':
                inputTimeParam = 'custom';
                gqlStartTime = format(start, 'yyyy-MM-dd');
                gqlEndTime = format(end, 'yyyy-MM-dd');
                break;
            default:
                console.error('Unknown activeDateFilter for Application API:', currentActiveDateFilter);
                setLoadingApplications(false);
                setErrorApplications('Invalid date filter configuration for applications.');
                return [];
        }

        const gqlQueryString = `
            query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
                getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
            }
            `;
        const variables = { resourceType: "application", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

        try {
            const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
            if (response.errors && response.errors.length > 0) {
                throw new Error(`GraphQL error fetching applications: ${response.errors.map(e => e.message).join(', ')}`);
            }
            if (!response.getCostDashboardData?.rowsData) {
                return [];
            }

            const apiResult = response.getCostDashboardData;
            if (apiResult.total_cost !== undefined) {
                setOverallApplicationTotalCostFromApi(apiResult.total_cost);
            } else {
                setOverallApplicationTotalCostFromApi(null);
            }
            if (apiResult.cost_difference !== undefined) {
                setOverallApplicationCostDifferenceFromApi(apiResult.cost_difference);
            } else {
                setOverallApplicationCostDifferenceFromApi(null);
            }
            const transformedData: FetchedApplicationData[] = (apiResult.rowsData as ApplicationRowDataRaw[]).map(appRaw => {
                const appName = appRaw.applicationName || `Unknown App (${appRaw.id})`; // Fallback for app name
                // Application must have cluster info to be processed
                if (!appRaw.cluster || !appRaw.cluster.clusterName) {
                    console.warn(`Application with ID ${appRaw.id} (${appName}) is missing cluster information. It will be excluded.`);
                    return null; // Mark for filtering
                }

                const costDataPoints: CostDataPoint[] = (appRaw.daily_costs_array || []).map(dailyCost => {
                    let dateStr = '';
                    try {
                        const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start); // Use query start date for year context
                        dateStr = format(parsedDate, 'yyyy-MM-dd');
                    } catch (e) {
                        console.error(`Error parsing application date string: "${dailyCost.usage_date}" for app ${appName} with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
                        dateStr = 'invalid-date';
                    }
                    return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
                }).filter(dp => dp.date !== 'invalid-date');

                return {
                    id: appRaw.id,
                    applicationName: appName,
                    clusterInfo: { id: appRaw.cluster.id, clusterName: appRaw.cluster.clusterName },
                    dailyCosts: costDataPoints,
                    totalCostForLabel: appRaw.total_cost_for_label, // ADDED
                    costDifferenceForLabel: appRaw.cost_difference_for_label, // ADDED
                };
            }).filter(app => app !== null && app.dailyCosts.length > 0) as FetchedApplicationData[]; // Filter out nulls and apps with no valid cost data
            return transformedData;

        } catch (err) {
            console.error("Error fetching or transforming application costs:", err);
            setErrorApplications(err instanceof Error ? err.message : 'An unknown error occurred while fetching application data');
            setOverallApplicationTotalCostFromApi(null); // ADDED
            setOverallApplicationCostDifferenceFromApi(null); // ADDED
            return [];
        } finally {
            setLoadingApplications(false);
        }
    },
    [] // No dependency
);
/**
 * Fetches service cost data from the GraphQL API.
 * @param start - The start date of the range.
 * @param end - The end date of the range.
 * @param currentActiveDateFilter - The active date filter type.
 * @returns A promise that resolves to an array of fetched service data.
 */
// const fetchServiceCostData = useCallback(
//     async (start: Date, end: Date, currentActiveDateFilter: string): Promise<FetchedServiceData[]> => {
//       setLoadingServices(true);
//       setErrorServices(null);
 
//       if (!start || !end || start > end) {
//         console.warn("Invalid date range for fetching service data:", start, end);
//         setLoadingServices(false);
//         return [];
//       }
 
//       let inputTimeParam: string;
//       let gqlStartTime: string | undefined = undefined;
//       let gqlEndTime: string | undefined = undefined;
 
//       switch (currentActiveDateFilter) {
//         case 'last30days': inputTimeParam = '30_days'; break;
//         case 'last7days': inputTimeParam = '7_days'; break;
//         case 'yesterday': inputTimeParam = 'yesterday'; break;
//         case 'custom':
//           inputTimeParam = 'custom';
//           gqlStartTime = format(start, 'yyyy-MM-dd');
//           gqlEndTime = format(end, 'yyyy-MM-dd');
//           break;
//         default:
//           console.error('Unknown activeDateFilter for Service API:', currentActiveDateFilter);
//           setLoadingServices(false);
//           setErrorServices('Invalid date filter configuration for services.');
//           return [];
//       }
 
//       const gqlQueryString = `
//         query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
//           getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
//         }
//       `;
//       const variables = { resourceType: "service", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };
 
//       try {
//         const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
//         if (response.errors && response.errors.length > 0) {
//           throw new Error(`GraphQL error fetching services: ${response.errors.map(e => e.message).join(', ')}`);
//         }
//         if (!response.getCostDashboardData?.rowsData) {
//           return [];
//         }
 
//         const apiResult = response.getCostDashboardData;
//         if (apiResult.total_cost !== undefined) {
//             setOverallServiceTotalCostFromApi(apiResult.total_cost);
//           } else {
//             setOverallServiceTotalCostFromApi(null);
//           }
//           if (apiResult.cost_difference !== undefined) {
//             setOverallServiceCostDifferenceFromApi(apiResult.cost_difference);
//           } else {
//             setOverallServiceCostDifferenceFromApi(null);
//           }
//         const transformedData: FetchedServiceData[] = (apiResult.rowsData as ServiceRowDataRaw[])
//           .map(serviceRaw => {
//             const serviceName = serviceRaw.serviceName || `Unknown Service (${serviceRaw.id})`;
 
//             if (!serviceRaw.application || !serviceRaw.application.applicationName || !serviceRaw.application.cluster || !serviceRaw.application.cluster.clusterName) {
//               console.warn(`Service with ID ${serviceRaw.id} (${serviceName}) is missing critical application or cluster information. It will be excluded.`);
//               return null; // Mark for filtering
//             }
 
//             const costDataPoints: CostDataPoint[] = (serviceRaw.daily_costs_array || []).map(dailyCost => {
//               let dateStr = '';
//               try {
//                 const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start);
//                 dateStr = format(parsedDate, 'yyyy-MM-dd');
//               } catch (e) {
//                 console.error(`Error parsing service date string: "${dailyCost.usage_date}" for service ${serviceName} with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
//                 dateStr = 'invalid-date';
//               }
//               return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
//             }).filter(dp => dp.date !== 'invalid-date');
 
//             return {
//                 id: serviceRaw.id,
//                 serviceName: serviceName,
//                 applicationInfo: {
//                     id: serviceRaw.application.id,
//                     applicationName: serviceRaw.application.applicationName,
//                     clusterId: serviceRaw.application.cluster.id,
//                     clusterName: serviceRaw.application.cluster.clusterName,
//                 },
//                 dailyCosts: costDataPoints,
//                 totalCostForLabel: serviceRaw.total_cost_for_label, // ADDED
//                 costDifferenceForLabel: serviceRaw.cost_difference_for_label, // ADDED
//             };
//           })
//           .filter(service => service !== null && service.dailyCosts.length > 0) as FetchedServiceData[];
//         return transformedData;
 
//       } catch (err) {
//         console.error("Error fetching or transforming service costs:", err);
//         setErrorServices(err instanceof Error ? err.message : 'An unknown error occurred while fetching service data');
//         setOverallServiceTotalCostFromApi(null); // ADDED
//         setOverallServiceCostDifferenceFromApi(null); // ADDED
//         return [];
//       } finally {
//         setLoadingServices(false);
//       }
//     },
//     [] // No direct state dependencies, but uses setLoadingServices, setErrorServices
//   );
const fetchServiceCostData = useCallback(
    async (start: Date, end: Date, currentActiveDateFilter: string): Promise<FetchedServiceData[]> => {
        setLoadingServices(true);
        setErrorServices(null);

        // --- NEW --- Mock data check
        if (USE_MOCK_DATA) {
            return new Promise(resolve => {
                setTimeout(() => {
                    const mockData = generateMockServiceData(start, end);
                    const totalCost = mockData.reduce((sum, svc) => sum + (svc.totalCostForLabel || 0), 0);
                    // Simulate overall cost summary fields
                    setOverallServiceTotalCostFromApi(totalCost);
                    const firstDayCost = mockData.map(d => d.dailyCosts[0]?.cost || 0).reduce((a, b) => a + b, 0);
                    const lastDayCost = mockData.map(d => d.dailyCosts[d.dailyCosts.length - 1]?.cost || 0).reduce((a, b) => a + b, 0);
                    setOverallServiceCostDifferenceFromApi(lastDayCost - firstDayCost);
                    setLoadingServices(false);
                    resolve(mockData);
                }, 300);
            });
        }
        // End of mock data block

        if (!start || !end || start > end) {
            console.warn("Invalid date range for fetching service data:", start, end);
            setLoadingServices(false);
            return [];
        }

        let inputTimeParam: string;
        let gqlStartTime: string | undefined = undefined;
        let gqlEndTime: string | undefined = undefined;

        switch (currentActiveDateFilter) {
            case 'last30days': inputTimeParam = '30_days'; break;
            case 'last7days': inputTimeParam = '7_days'; break;
            case 'yesterday': inputTimeParam = 'yesterday'; break;
            case 'custom':
                inputTimeParam = 'custom';
                gqlStartTime = format(start, 'yyyy-MM-dd');
                gqlEndTime = format(end, 'yyyy-MM-dd');
                break;
            default:
                console.error('Unknown activeDateFilter for Service API:', currentActiveDateFilter);
                setLoadingServices(false);
                setErrorServices('Invalid date filter configuration for services.');
                return [];
        }

        const gqlQueryString = `
        query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
            getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
        }
        `;
        const variables = { resourceType: "service", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

        try {
            const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
            if (response.errors && response.errors.length > 0) {
                throw new Error(`GraphQL error fetching services: ${response.errors.map(e => e.message).join(', ')}`);
            }
            if (!response.getCostDashboardData?.rowsData) {
                return [];
            }

            const apiResult = response.getCostDashboardData;
            if (apiResult.total_cost !== undefined) {
                setOverallServiceTotalCostFromApi(apiResult.total_cost);
            } else {
                setOverallServiceTotalCostFromApi(null);
            }
            if (apiResult.cost_difference !== undefined) {
                setOverallServiceCostDifferenceFromApi(apiResult.cost_difference);
            } else {
                setOverallServiceCostDifferenceFromApi(null);
            }
            const transformedData: FetchedServiceData[] = (apiResult.rowsData as ServiceRowDataRaw[])
                .map(serviceRaw => {
                    const serviceName = serviceRaw.serviceName || `Unknown Service (${serviceRaw.id})`;

                    if (!serviceRaw.application || !serviceRaw.application.applicationName || !serviceRaw.application.cluster || !serviceRaw.application.cluster.clusterName) {
                        console.warn(`Service with ID ${serviceRaw.id} (${serviceName}) is missing critical application or cluster information. It will be excluded.`);
                        return null; // Mark for filtering
                    }

                    const costDataPoints: CostDataPoint[] = (serviceRaw.daily_costs_array || []).map(dailyCost => {
                        let dateStr = '';
                        try {
                            const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start);
                            dateStr = format(parsedDate, 'yyyy-MM-dd');
                        } catch (e) {
                            console.error(`Error parsing service date string: "${dailyCost.usage_date}" for service ${serviceName} with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
                            dateStr = 'invalid-date';
                        }
                        return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
                    }).filter(dp => dp.date !== 'invalid-date');

                    return {
                        id: serviceRaw.id,
                        serviceName: serviceName,
                        applicationInfo: {
                            id: serviceRaw.application.id,
                            applicationName: serviceRaw.application.applicationName,
                            clusterId: serviceRaw.application.cluster.id,
                            clusterName: serviceRaw.application.cluster.clusterName,
                        },
                        dailyCosts: costDataPoints,
                        totalCostForLabel: serviceRaw.total_cost_for_label, // ADDED
                        costDifferenceForLabel: serviceRaw.cost_difference_for_label, // ADDED
                    };
                })
                .filter(service => service !== null && service.dailyCosts.length > 0) as FetchedServiceData[];
            return transformedData;

        } catch (err) {
            console.error("Error fetching or transforming service costs:", err);
            setErrorServices(err instanceof Error ? err.message : 'An unknown error occurred while fetching service data');
            setOverallServiceTotalCostFromApi(null); // ADDED
            setOverallServiceCostDifferenceFromApi(null); // ADDED
            return [];
        } finally {
            setLoadingServices(false);
        }
    },
    []
);

//   const fetchAddonCostData = useCallback(
//     async (start: Date, end: Date, currentActiveDateFilter: string): Promise<FetchedAddonData[]> => {
//         setLoadingAddonCosts(true);
//         setErrorAddonCosts(null);

//         if (!start || !end || start > end) {
//             console.warn("Invalid date range for fetching addon data:", start, end);
//             setLoadingAddonCosts(false);
//             return [];
//         }

//         let inputTimeParam: string;
//         let gqlStartTime: string | undefined = undefined;
//         let gqlEndTime: string | undefined = undefined;

//         switch (currentActiveDateFilter) {
//             case 'last30days': inputTimeParam = '30_days'; break;
//             case 'last7days': inputTimeParam = '7_days'; break;
//             case 'yesterday': inputTimeParam = 'yesterday'; break;
//             case 'custom':
//                 inputTimeParam = 'custom';
//                 gqlStartTime = format(start, 'yyyy-MM-dd');
//                 gqlEndTime = format(end, 'yyyy-MM-dd');
//                 break;
//             default:
//                 console.error('Unknown activeDateFilter for Addon API:', currentActiveDateFilter);
//                 setLoadingAddonCosts(false);
//                 setErrorAddonCosts('Invalid date filter configuration for addons.');
//                 return [];
//         }

//         const gqlQueryString = `
//           query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
//             getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
//           }
//         `;
//         // Note: The prompt shows variables without startTime/endTime for 30_days, matching existing fetch functions.
//         // The GQL query itself includes them as optional.
//         const variables = { resourceType: "addon", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

//         try {
//             const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
//             if (response.errors && response.errors.length > 0) {
//                 throw new Error(`GraphQL error fetching addons: ${response.errors.map(e => e.message).join(', ')}`);
//             }
//             if (!response.getCostDashboardData?.rowsData) {
//                 return [];
//             }

//             const apiResult = response.getCostDashboardData;
//             const transformedData: FetchedAddonData[] = (apiResult.rowsData as AddonRowDataRaw[])
//                 .map(addonRaw => {
//                     const serviceDesc = addonRaw.service_description || `Unknown Addon (${addonRaw.id})`;

//                     // Ensure essential linking information is present
//                     if (!addonRaw.cluster?.clusterName || !addonRaw.applicationName) {
//                         console.warn(`Addon with ID ${addonRaw.id} ("${serviceDesc}") is missing cluster or application name. It will be excluded.`);
//                         return null;
//                     }

//                     const costDataPoints: CostDataPoint[] = (addonRaw.daily_costs_array || []).map(dailyCost => {
//                         let dateStr = '';
//                         try {
//                             // Use the start date of the query for year context, as done for other data types
//                             const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start);
//                             dateStr = format(parsedDate, 'yyyy-MM-dd');
//                         } catch (e) {
//                             console.error(`Error parsing addon date string: "${dailyCost.usage_date}" for addon ${serviceDesc} with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
//                             dateStr = 'invalid-date';
//                         }
//                         return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
//                     }).filter(dp => dp.date !== 'invalid-date');

//                     if (costDataPoints.length === 0 && (addonRaw.daily_costs_array || []).length > 0) {
//                         // This means all date points were invalid
//                         console.warn(`Addon "${serviceDesc}" (ID: ${addonRaw.id}) had date points, but none were parseable or valid for the current range context. Excluding.`);
//                         return null;
//                     }
//                     // If costDataPoints is empty because daily_costs_array was empty/null, it's also fine to exclude or include based on preference.
//                     // For charting, items with no cost data points are usually excluded.
//                      if (costDataPoints.length === 0) return null;


//                     return {
//                         id: addonRaw.id,
//                         serviceDescription: serviceDesc,
//                         applicationName: addonRaw.applicationName,
//                         clusterName: addonRaw.cluster.clusterName,
//                         dailyCosts: costDataPoints,
//                     };
//                 })
//                 .filter(p => p !== null) as FetchedAddonData[]; // Ensure nulls are filtered out
//             return transformedData;

//         } catch (err) {
//             console.error("Error fetching or transforming addon costs:", err);
//             setErrorAddonCosts(err instanceof Error ? err.message : 'An unknown error occurred while fetching addon data');
//             return [];
//         } finally {
//             setLoadingAddonCosts(false);
//         }
//     },
//     [] // Dependencies: queryGraphql, setLoadingAddonCosts, setErrorAddonCosts. Format, parse are global.
// );

// Effect to fetch cluster data when date range or filter changes
const fetchAddonCostData = useCallback(
    async (start: Date, end: Date, currentActiveDateFilter: string): Promise<FetchedAddonData[]> => {
        setLoadingAddonCosts(true);
        setErrorAddonCosts(null);

        // --- NEW --- Mock data check
        if (USE_MOCK_DATA) {
            return new Promise(resolve => {
                setTimeout(() => {
                    const mockData = generateMockAddonData(start, end);
                    setLoadingAddonCosts(false);
                    resolve(mockData);
                }, 300);
            });
        }
        // End of mock data block

        if (!start || !end || start > end) {
            console.warn("Invalid date range for fetching addon data:", start, end);
            setLoadingAddonCosts(false);
            return [];
        }

        let inputTimeParam: string;
        let gqlStartTime: string | undefined = undefined;
        let gqlEndTime: string | undefined = undefined;

        switch (currentActiveDateFilter) {
            case 'last30days': inputTimeParam = '30_days'; break;
            case 'last7days': inputTimeParam = '7_days'; break;
            case 'yesterday': inputTimeParam = 'yesterday'; break;
            case 'custom':
                inputTimeParam = 'custom';
                gqlStartTime = format(start, 'yyyy-MM-dd');
                gqlEndTime = format(end, 'yyyy-MM-dd');
                break;
            default:
                console.error('Unknown activeDateFilter for Addon API:', currentActiveDateFilter);
                setLoadingAddonCosts(false);
                setErrorAddonCosts('Invalid date filter configuration for addons.');
                return [];
        }

        const gqlQueryString = `
        query Query($resourceType: String!, $inputTime: String!, $startTime: String, $endTime: String) {
            getCostDashboardData(resourceType: $resourceType, inputTime: $inputTime, startTime: $startTime, endTime: $endTime)
        }
        `;
        const variables = { resourceType: "addon", inputTime: inputTimeParam, startTime: gqlStartTime, endTime: gqlEndTime };

        try {
            const response: GraphQLApiResponse = await queryGraphql(gqlQueryString, variables);
            if (response.errors && response.errors.length > 0) {
                throw new Error(`GraphQL error fetching addons: ${response.errors.map(e => e.message).join(', ')}`);
            }
            if (!response.getCostDashboardData?.rowsData) {
                return [];
            }

            const apiResult = response.getCostDashboardData;
            const transformedData: FetchedAddonData[] = (apiResult.rowsData as AddonRowDataRaw[])
                .map(addonRaw => {
                    const serviceDesc = addonRaw.service_description || `Unknown Addon (${addonRaw.id})`;

                    if (!addonRaw.cluster?.clusterName || !addonRaw.applicationName) {
                        console.warn(`Addon with ID ${addonRaw.id} ("${serviceDesc}") is missing cluster or application name. It will be excluded.`);
                        return null;
                    }

                    const costDataPoints: CostDataPoint[] = (addonRaw.daily_costs_array || []).map(dailyCost => {
                        let dateStr = '';
                        try {
                            const parsedDate = parse(dailyCost.usage_date, 'MMMM dd', start);
                            dateStr = format(parsedDate, 'yyyy-MM-dd');
                        } catch (e) {
                            console.error(`Error parsing addon date string: "${dailyCost.usage_date}" for addon ${serviceDesc} with start context ${format(start, 'yyyy-MM-dd')}. Error:`, e);
                            dateStr = 'invalid-date';
                        }
                        return { date: dateStr, cost: typeof dailyCost.daily_total_cost === 'number' ? dailyCost.daily_total_cost : 0, };
                    }).filter(dp => dp.date !== 'invalid-date');

                    if (costDataPoints.length === 0 && (addonRaw.daily_costs_array || []).length > 0) {
                        console.warn(`Addon "${serviceDesc}" (ID: ${addonRaw.id}) had date points, but none were parseable or valid for the current range context. Excluding.`);
                        return null;
                    }
                    if (costDataPoints.length === 0) return null;


                    return {
                        id: addonRaw.id,
                        serviceDescription: serviceDesc,
                        applicationName: addonRaw.applicationName,
                        clusterName: addonRaw.cluster.clusterName,
                        dailyCosts: costDataPoints,
                    };
                })
                .filter(p => p !== null) as FetchedAddonData[];
            return transformedData;

        } catch (err) {
            console.error("Error fetching or transforming addon costs:", err);
            setErrorAddonCosts(err instanceof Error ? err.message : 'An unknown error occurred while fetching addon data');
            return [];
        } finally {
            setLoadingAddonCosts(false);
        }
    },
    []
);

useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchClusterCostData(dateRange.startDate, dateRange.endDate, activeDateFilter)
        .then(data => {
          setAllClusterData(data);
          const clusterNames = data.map(c => c.clusterName);
          setAvailableClusters(clusterNames);
          
          // Update selected clusters: keep existing if still valid, else select initial set
          setSelectedClusterNames(prevSelected => {
            const stillAvailableSelections = prevSelected.filter(name => clusterNames.includes(name));
            if (stillAvailableSelections.length > 0) {
              setStagedClusterSelection(stillAvailableSelections);
              return stillAvailableSelections;
            } else {
              // Select up to 4 clusters by default if previous selection is empty or invalid
              const initialSelection = clusterNames.slice(0, Math.min(4, clusterNames.length));
              setStagedClusterSelection(initialSelection);
              return initialSelection;
            }
          });

          // Generate and set colors for the cluster chart
          const colors = generateColors(clusterNames.length);
          const colorMap: Record<string, string> = {};
          clusterNames.forEach((name, index) => { colorMap[name] = colors[index]; });
          setClusterChartColors(colorMap);
        })
        .catch(() => { /* Error handling is inside fetchClusterCostData */ });
    } else {
      // Clear cluster data if date range is invalid
      setAllClusterData([]); setAvailableClusters([]); setSelectedClusterNames([]); setStagedClusterSelection([]);
      setLoadingClusters(false); setErrorClusters(null);
    }
  }, [dateRange, fetchClusterCostData, activeDateFilter]); // Dependencies: dateRange, fetch function, and active filter

// Effect for fetching application data when date range, filter, or selected clusters change
useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && selectedClusterNames.length > 0) {
        fetchApplicationCostData(dateRange.startDate, dateRange.endDate, activeDateFilter)
            .then(fetchedApps => {
                setAllApplicationRawData(fetchedApps);
                // Filter raw application data to include only those in selected clusters
                const appsInSelectedClusters = fetchedApps.filter(app =>
                    selectedClusterNames.includes(app.clusterInfo.clusterName)
                );

                // Get unique application names from the filtered list
                const appNames = Array.from(new Set(appsInSelectedClusters.map(app => app.applicationName))).sort();
                setAvailableApplications(appNames);

                // Update selected applications: keep existing if still valid, else default to "all" (empty array)
                setSelectedApplicationNames(prevSelectedApps => {
                    const validSelectedApps = prevSelectedApps.filter(name => appNames.includes(name));
                    setStagedApplicationSelection(validSelectedApps); // Update staged selection
                    return validSelectedApps; // Empty array means all applications
                });
            })
            .catch(() => { /* Error handling inside fetchApplicationCostData */ });
    } else {
        // Clear application data if no valid date range or no clusters selected
        setAllApplicationRawData([]);
        setAvailableApplications([]);
        setSelectedApplicationNames([]);
        setStagedApplicationSelection([]);
        setProcessedApplicationData([]); // Clear processed data too
        if (selectedClusterNames.length === 0) {
             setLoadingApplications(false); // Stop loading if no clusters are selected
             setErrorApplications(null); // Clear any application errors
        }
    }
}, [dateRange, activeDateFilter, selectedClusterNames, fetchApplicationCostData]); // Dependencies for fetching


// Effect for processing application data for display whenever relevant states change
useEffect(() => {
    // Guard conditions: ensure necessary data is present
    if (!dateRange.startDate || !dateRange.endDate || allApplicationRawData.length === 0 || selectedClusterNames.length === 0) {
        setProcessedApplicationData([]); // Clear if no data to process
        return;
    }

    // Generate an array of all dates in the selected range
    const dateArray = eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate });

    // Filter applications: must be in a selected cluster AND (if any apps are selected) in the selected app list
    const appsToProcess = allApplicationRawData.filter(app =>
        selectedClusterNames.includes(app.clusterInfo.clusterName) &&
        (selectedApplicationNames.length === 0 || selectedApplicationNames.includes(app.applicationName)) // If no apps selected, include all from selected clusters
    );

    // Group applications by their cluster name
    const groupedByCluster: Record<string, FetchedApplicationData[]> = {};
    appsToProcess.forEach(app => {
        const clusterName = app.clusterInfo.clusterName;
        if (!groupedByCluster[clusterName]) {
            groupedByCluster[clusterName] = [];
        }
        groupedByCluster[clusterName].push(app);
    });

    // Process each group of applications (per cluster)
    const finalProcessedData: ProcessedClusterWithApps[] = Object.entries(groupedByCluster)
        .map(([clusterName, appsInCluster]) => {
            const processedApps: ProcessedApplicationItem[] = appsInCluster.map(app => {
                const appDailyCostsMap = new Map(app.dailyCosts.map(dc => [dc.date, dc.cost]));
                const costTrendData: number[] = dateArray.map(day => {
                    const dayStr = format(day, 'yyyy-MM-dd');
                    const costVal = appDailyCostsMap.get(dayStr) || 0;
                    return costVal;
                });

                // Use totalCostForLabel and costDifferenceForLabel from FetchedApplicationData
                const totalCost = app.totalCostForLabel ?? app.dailyCosts.reduce((sum, dc) => sum + dc.cost, 0); // Fallback to sum if not provided
                const costChange = app.costDifferenceForLabel ?? 0; // Fallback to 0 if not provided

                const maxCostInTrend = costTrendData.length > 0 ? Math.max(...costTrendData, 0.0000001) : 0.0000001;

                return {
                    appName: app.applicationName,
                    totalCost, // UPDATED
                    costChange, // UPDATED
                    costTrendData,
                    maxCostInTrend,
                };
            }).sort((a, b) => b.totalCost - a.totalCost); // Sort applications within a cluster by total cost descending

            return {
                clusterName,
                applications: processedApps,
            };
        })
        .filter(clusterGroup => clusterGroup.applications.length > 0) // Remove cluster groups with no applications after filtering
        .sort((a, b) => a.clusterName.localeCompare(b.clusterName)); // Sort cluster groups by name

    setProcessedApplicationData(finalProcessedData);

}, [allApplicationRawData, selectedClusterNames, selectedApplicationNames, dateRange]); // Dependencies for processing


// Effect for fetching service data when date range, filter, selected clusters, or selected applications change
useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) ) { // Fetch if clusters are selected and either apps are selected or apps are available (meaning "All Apps" is a valid context)
      // Condition: Only fetch if we have clusters and a context for applications (either specific ones or "all")
      const shouldFetchServices = selectedClusterNames.length > 0 &&
                                  (selectedApplicationNames.length > 0 || (allApplicationRawData.length > 0 && availableApplications.length > 0));
 
 
      if (shouldFetchServices) {
          fetchServiceCostData(dateRange.startDate, dateRange.endDate, activeDateFilter)
          .then(fetchedServices => {
              setAllServiceRawData(fetchedServices);
 
              // Filter raw service data to include only those in selected clusters and (if specified) selected applications
              const servicesToConsider = fetchedServices.filter(service =>
              selectedClusterNames.includes(service.applicationInfo.clusterName) &&
              (selectedApplicationNames.length === 0 || selectedApplicationNames.includes(service.applicationInfo.applicationName))
              );
 
              const serviceNames = Array.from(new Set(servicesToConsider.map(service => service.serviceName))).sort();
              setAvailableServices(serviceNames);
 
              setSelectedServiceNames(prevSelectedServices => {
              const validSelectedServices = prevSelectedServices.filter(name => serviceNames.includes(name));
              setStagedServiceSelection(validSelectedServices); // Default to current valid selections or empty (all)
              return validSelectedServices;
              });
          })
          .catch(() => { /* Error handling inside fetchServiceCostData */ });
      } else {
           // Clear service data if dependent selections are missing
          setAllServiceRawData([]);
          setAvailableServices([]);
          setSelectedServiceNames([]);
          setStagedServiceSelection([]);
          setProcessedAndGroupedServiceData({});
          setLoadingServices(false);
          setErrorServices(null);
      }
    } else {
      setAllServiceRawData([]);
      setAvailableServices([]);
      setSelectedServiceNames([]);
      setStagedServiceSelection([]);
      setProcessedAndGroupedServiceData({});
      if (selectedClusterNames.length === 0 || (availableApplications.length === 0 && selectedApplicationNames.length === 0 && !loadingApplications) ) {
           setLoadingServices(false);
           setErrorServices(null);
      }
    }
  }, [dateRange, activeDateFilter, selectedClusterNames, selectedApplicationNames, fetchServiceCostData, allApplicationRawData, availableApplications, loadingApplications]);


// Effect for processing service data for display
useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate || allServiceRawData.length === 0 || selectedClusterNames.length === 0) {
      setProcessedAndGroupedServiceData({});
      return;
    }
 
    const dateArray = eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate });
 
    const servicesToProcess = allServiceRawData.filter(service =>
      selectedClusterNames.includes(service.applicationInfo.clusterName) &&
      (selectedApplicationNames.length === 0 || selectedApplicationNames.includes(service.applicationInfo.applicationName)) &&
      (selectedServiceNames.length === 0 || selectedServiceNames.includes(service.serviceName))
    );
 
    const groupedData: GroupedServiceData = {};
 
    servicesToProcess.forEach(service => {
      const { clusterName, applicationName } = service.applicationInfo;
 
      const serviceDailyCostsMap = new Map(service.dailyCosts.map(dc => [dc.date, dc.cost]));
    const costTrendData: number[] = dateArray.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const costVal = serviceDailyCostsMap.get(dayStr) || 0;
        return costVal;
    });

    // Use totalCostForLabel and costDifferenceForLabel from FetchedServiceData
    const totalCost = service.totalCostForLabel ?? service.dailyCosts.reduce((sum, dc) => sum + dc.cost, 0); // Fallback to sum
    const costChange = service.costDifferenceForLabel ?? 0; // Fallback to 0

    const maxCostInTrend = costTrendData.length > 0 ? Math.max(...costTrendData, 0.0000001) : 0.0000001;

    const processedService: ProcessedServiceItem = {
        serviceName: service.serviceName,
        totalCost, // UPDATED
        costChange, // UPDATED
        costTrendData,
        maxCostInTrend,
    };
 
      if (!groupedData[clusterName]) {
        groupedData[clusterName] = {};
      }
      if (!groupedData[clusterName][applicationName]) {
        groupedData[clusterName][applicationName] = [];
      }
      groupedData[clusterName][applicationName].push(processedService);
    });
 
    // Sort services within each app by total cost
    for (const cluster in groupedData) {
      for (const app in groupedData[cluster]) {
        groupedData[cluster][app].sort((a, b) => b.totalCost - a.totalCost);
      }
    }
    // Optional: Sort applications within clusters, or clusters themselves if needed
    // For now, the structure is as built. If you need specific sorting order for clusters/apps:
    // const sortedGroupedData: GroupedServiceData = {};
    // Object.keys(groupedData).sort().forEach(clusterName => {
    //     sortedGroupedData[clusterName] = {};
    //     Object.keys(groupedData[clusterName]).sort().forEach(appName => {
    //       sortedGroupedData[clusterName][appName] = groupedData[clusterName][appName];
    //     });
    // });
    // setProcessedAndGroupedServiceData(sortedGroupedData);
 
 
    setProcessedAndGroupedServiceData(groupedData);
 
  }, [allServiceRawData, selectedClusterNames, selectedApplicationNames, selectedServiceNames, dateRange]);

  // Effect for fetching addon data
useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && selectedClusterNames.length > 0) {
        // Fetching is primarily dependent on date range and selected clusters.
        // Application-level filtering will happen during the processing step for the chart.
        fetchAddonCostData(dateRange.startDate, dateRange.endDate, activeDateFilter)
            .then(fetchedAddons => {
                setAllAddonRawData(fetchedAddons);
            })
            .catch(() => { /* Error handling is inside fetchAddonCostData */ });
    } else {
        setAllAddonRawData([]);
        setAddonChartData({ labels: [], datasets: [] }); // Clear chart if no clusters
        if (selectedClusterNames.length === 0) { // Only clear loading/error if dependent data is missing
             setLoadingAddonCosts(false);
             setErrorAddonCosts(null);
        }
    }
}, [dateRange, activeDateFilter, selectedClusterNames, fetchAddonCostData]);

const addonChartOptions = useMemo((): ChartOptions<'bar'> => ({ // Type is 'bar'
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'bottom', // As per typical UI, can be adjusted
            align:'start',
            labels: {
                boxWidth: 12,
                padding: 15,
                font: { size: 11, family: "Gilroy-SemiBold" },
                usePointStyle: true,
                pointStyle: 'circle', // Bar charts often use 'rect'
            },
            onClick: () => {},
        },
        title: { display: false },
        tooltip: { // Standard tooltip, customize if needed
            enabled: false, // <= ADD THIS to disable default tooltip
            external: CustomHtmlLineTooltip, // <= ADD THIS to use your custom tooltip function
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function(context) {
                    let label = context.dataset.label || '';
                    if (label) {
                        label += ': ';
                    }
                    if (context.parsed.y !== null) {
                        label += formatCurrencyWithSmallValues(context.parsed.y); // Use existing detailed formatter
                    }
                    return label;
                },
                title: function(tooltipItems) {
                    if (tooltipItems.length > 0) {
                        // Assuming X-axis labels are dates in 'yyyy-MM-dd' format passed to chart data
                        const dateStr = tooltipItems[0].label;
                        try {
                            const date = parse(dateStr, 'yyyy-MM-dd', new Date()); // Parse the date string from the label
                             if (!isNaN(date.getTime())) {
                                 return format(date, 'PPP'); // e.g., "May 14, 2025"
                            }
                        } catch (e) {
                            console.error("Error parsing date for tooltip title:", dateStr, e);
                            return dateStr; // Fallback to the raw label
                        }
                    }
                    return '';
                }
            }
        },
    },
    scales: {
        x: {
            type: 'time', // X-axis represents time
            time: {
                unit: 'day',
                parser: 'yyyy-MM-dd', // How the labels (dates) are parsed
                tooltipFormat: 'PPP',  // Format for dates in tooltip title
                displayFormats: {
                    day: 'MMM d' // Format for dates on the x-axis
                }
            },
            grid: { display: false },
            ticks: { color: 'grey.600', font: { size: 12, family: 'Gilroy-Regular' } },
            stacked: true, // Stack bars for different service_descriptions on the same day
        },
        y: {
            beginAtZero: true,
            grid: { display: true, color: 'rgba(200, 200, 200, 0.2)' }, // Optional: light grid lines
            ticks: {
                color: 'grey.600',
                font: { size: 12, family: 'Gilroy-Regular' },
                callback: function(value: string | number) { // Use existing currency formatter
                    if (typeof value === 'number') {
                        if (value >= 1000000) { return (value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1) + 'M'; }
                        if (value >= 1000) { return (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1) + 'K'; }
                        return formatCurrencyWithSmallValues(value);
                    }
                    return value;
                }
            },
            stacked: true, // Ensure Y-axis is also marked as stacked
        },
    },
    interaction: {
        mode: 'index',
        intersect: false,
    },
    animation: { duration: 0 }, // Match existing chart animation style
}), []); // dateRange might be needed if tooltipFormat changes dynamically based on range length

// Effect for processing addon data for the bar chart
useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate || allAddonRawData.length === 0 || selectedClusterNames.length === 0) {
        setAddonChartData({ labels: [], datasets: [] });
        return;
    }

    const chartStartDate = dateRange.startDate;
    const chartEndDate = dateRange.endDate;

    // 1. Filter addons: must be in selected clusters AND (if apps selected) in selected apps
    const relevantAddons = allAddonRawData.filter(p =>
        selectedClusterNames.includes(p.clusterName) &&
        (selectedApplicationNames.length === 0 || selectedApplicationNames.includes(p.applicationName))
    );

    if (relevantAddons.length === 0) {
        setAddonChartData({ labels: [], datasets: [] });
        return;
    }

    // 2. Get all unique dates in the range for chart labels
    const dateLabels = eachDayOfInterval({ start: chartStartDate, end: chartEndDate })
        .map(date => format(date, 'yyyy-MM-dd'));

    // 3. Collect unique service descriptions (for legends/datasets)
    const uniqueServiceDescriptions = Array.from(new Set(relevantAddons.map(p => p.serviceDescription))).sort();

    // 4. Map costs: serviceDescription -> date -> totalCost
    const costsMap: Record<string, Record<string, number>> = {};
    uniqueServiceDescriptions.forEach(desc => costsMap[desc] = {});

    relevantAddons.forEach(addon => {
        addon.dailyCosts.forEach(costPoint => {
            // Ensure the date is within our chart's current viewable range
            if (dateLabels.includes(costPoint.date)) {
                 if (!costsMap[addon.serviceDescription]) { // Should already be initialized but defensive
                     costsMap[addon.serviceDescription] = {};
                 }
                costsMap[addon.serviceDescription][costPoint.date] =
                    (costsMap[addon.serviceDescription][costPoint.date] || 0) + costPoint.cost;
            }
        });
    });

    // 5. Generate datasets
    const serviceColors = generateColors(uniqueServiceDescriptions.length);
    const datasets = uniqueServiceDescriptions.map((serviceDesc, index) => {
        const dataForService = dateLabels.map(date => costsMap[serviceDesc]?.[date] || 0);
        const color = serviceColors[index % serviceColors.length];
        return {
            label: serviceDesc,
            data: dataForService,
            backgroundColor: color, // Use full color for bars
            borderColor: color,     // Border for bars (optional)
            borderWidth: 1,         // Optional
        };
    });

    setAddonChartData({
        labels: dateLabels,
        datasets: datasets,
    });

}, [allAddonRawData, dateRange, selectedClusterNames, selectedApplicationNames /* generateColors is stable */]);

// NEW: Dummy Chart Options for AI Agent and Notebooks
const aiAgentChartOptions = useMemo((): ChartOptions<'bar'> => ({ ...addonChartOptions }), [addonChartOptions]);
const notebooksChartOptions = useMemo((): ChartOptions<'bar'> => ({ ...addonChartOptions }), [addonChartOptions]);

// --- NEW --- Effect for generating random Non-K8s data
// --- NEW --- Effect for generating random Non-K8s data
//  useEffect(() => {
//      if (resourceType === 'non-k8s' && dateRange.startDate && dateRange.endDate) {
//          setLoadingNonK8s(true);
 
//          const dateLabels = eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate }).map(d => format(d, 'yyyy-MM-dd'));
//          const trendLength = dateLabels.length;
 
//          const serviceNamePools = {
//              'AI Agent': ['Cognitive Search Service', 'QnA Bot Service', 'Language Understanding Pod'],
//              'RAG': ['Vector Database Service', 'Document Chunking Pipeline', 'Embedding Model Service'],
//              'Fine Tuning': ['GPT-3 Fine-Tune Job', 'BERT Training Instance'],
//              'Notebooks': ['JupyterHub Environment', 'Data-Science VM'],
//              'Data Pipelines': ['Daily Sales ETL', 'User Activity Stream', 'Nightly Batch Processor'],
//              'Data Warehouse': ['Reporting DB', 'Staging Warehouse', 'Analytics Main DB'],
//              'Analytics & BI': ['Metabase Instance', 'PowerBI Gateway', 'Looker Connection'],
//          };
         
//          const generateRandomTrend = (maxVal: number) => Array.from({ length: trendLength }, () => Math.random() * maxVal);
 
//          const processCategory = (category: AiSolutionCategory | DataSolutionCategory, maxCost: number): ProcessedNonK8sItem[] => {
//              const serviceNames = serviceNamePools[category] || [`${category} Service 1`, `${category} Service 2`];
//              return serviceNames.map(name => {
//                  const costTrendData = generateRandomTrend(maxCost / trendLength / serviceNames.length);
//                  const totalCost = costTrendData.reduce((sum, val) => sum + val, 0);
//                  const costChange = (Math.random() - 0.4) * totalCost * 0.25; // Skew towards positive change
//                  const maxCostInTrend = Math.max(...costTrendData, 0.000001);
//                  return { name, totalCost, costChange, costTrendData, maxCostInTrend };
//              });
//          };
 
//          setTimeout(() => {
//              const aiSolutionsData: NonK8sCategoryData = {};
//              AI_SOLUTION_CATEGORIES.forEach(cat => { aiSolutionsData[cat] = processCategory(cat, 1200); });
//              setProcessedAiSolutionsData(aiSolutionsData);
 
//              const dataSolutionsData: NonK8sCategoryData = {};
//              DATA_SOLUTION_CATEGORIES.forEach(cat => { dataSolutionsData[cat] = processCategory(cat, 950); });
//              setProcessedDataSolutionsData(dataSolutionsData);
             
//              const aiTotal = Object.values(aiSolutionsData).flat().reduce((sum, item) => sum + item.totalCost, 0);
//              const dataTotal = Object.values(dataSolutionsData).flat().reduce((sum, item) => sum + item.totalCost, 0);
 
//              setNonK8sChartData({
//                  labels: dateLabels,
//                  datasets: [
//                      { label: 'AI Solutions', data: generateRandomTrend(aiTotal/trendLength), borderColor: '#6495ED', tension: 0.4, fill: false, pointRadius: 0, borderWidth: 2 },
//                      { label: 'Data Solutions', data: generateRandomTrend(dataTotal/trendLength), borderColor: '#FFB74D', tension: 0.4, fill: false, pointRadius: 0, borderWidth: 2 },
//                  ]
//              });
//              setLoadingNonK8s(false);
//          }, 800);
//      }
//  }, [resourceType, dateRange]);
useEffect(() => {
    if (resourceType === 'non-k8s' && dateRange.startDate && dateRange.endDate) {
        setLoadingNonK8s(true);

        // Guard clause to ensure dates are not null and satisfy TypeScript
        if (!dateRange.startDate || !dateRange.endDate) {
            setLoadingNonK8s(false);
            return;
        }
        const start = dateRange.startDate;
        const end = dateRange.endDate;

        // The mock data logic is now the only path
        const dateLabels = eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'));
        
        // --- EDIT your non-k8s manual costs here ---
        const serviceNamePools = {
    "AI Agent": [
        { 
            "name": "data-monitoring-agent", 
            "costs": [156, 172, 169, 137, 192, 175, 134, 185, 199, 142, 172, 177, 188, 133, 190, 166, 179, 131, 141, 133, 175, 131, 131, 142, 190, 155, 179, 180, 169, 171] 
        }, 
        { 
            "name": "ops-assistant", 
            "costs": [188, 145, 178, 146, 187, 164, 130, 180, 139, 184, 157, 198, 179, 189, 151, 198, 165, 170, 192, 175, 185, 172, 130, 162, 190, 160, 132, 157, 153, 163] 
        }
    ],
    "RAG": [
        { 
            "name": "veritasai", 
            "costs": [182, 187, 191, 198, 190, 184, 173, 167, 162, 158, 180, 179, 171, 156, 195, 166, 135, 154, 137, 199, 175, 198, 137, 172, 150, 134, 160, 191, 196, 169] 
        }
    ],
    "Fine Tuning": [],
    "Notebooks": [
        { 
            "name": "recommend-engine", 
            "costs": [160, 197, 179, 159, 196, 168, 190, 148, 151, 169, 147, 186, 166, 181, 166, 161, 191, 133, 154, 182, 154, 154, 149, 178, 143, 147, 162, 198, 199, 174] 
        }
    ],
    "Data Pipelines": [
        { 
            "name": "data-proctor", 
            "costs": [164, 185, 185, 191, 155, 179, 180, 195, 149, 179, 190, 198, 172, 159, 196, 186, 189, 185, 155, 179, 198, 144, 154, 178, 130, 177, 169, 149, 176, 182] 
        }
    ],
    "Data Warehouse": [
        { 
            "name": "production-workloads", 
            "costs": [185, 198, 169, 144, 152, 160, 152, 168, 148, 161, 131, 142, 179, 182, 186, 186, 148, 146, 193, 162, 178, 187, 141, 169, 144, 152, 133, 160, 154, 198] 
        }
    ],
    "Analytics & BI": []
}
        
        const processCategory = (category: AiSolutionCategory | DataSolutionCategory): ProcessedNonK8sItem[] => {
            const services = serviceNamePools[category] || [];
            return services.map(service => {
                const costTrendDataPoints = getMockCostDataForRange(start, end, service.costs);
                const costTrendData = costTrendDataPoints.map(d => d.cost);
                const totalCost = costTrendData.reduce((sum, val) => sum + val, 0);
                const costChange = costTrendData.length > 1 ? costTrendData[costTrendData.length - 1] - costTrendData[0] : 0;
                const maxCostInTrend = Math.max(...costTrendData, 0.000001);
                return { name: service.name, totalCost, costChange, costTrendData, maxCostInTrend };
            });
        };

        setTimeout(() => {
            const aiSolutionsData: NonK8sCategoryData = {};
            AI_SOLUTION_CATEGORIES.forEach(cat => { aiSolutionsData[cat] = processCategory(cat); });
            setProcessedAiSolutionsData(aiSolutionsData);

            const dataSolutionsData: NonK8sCategoryData = {};
            DATA_SOLUTION_CATEGORIES.forEach(cat => { dataSolutionsData[cat] = processCategory(cat); });
            setProcessedDataSolutionsData(dataSolutionsData);
            
            const aiDailyCosts = getMockCostDataForRange(start, end, serviceNamePools['AI Agent'][0].costs); // Use one as representative trend
            const dataDailyCosts = getMockCostDataForRange(start, end, serviceNamePools['Data Pipelines'][0].costs);

            setNonK8sChartData({
                labels: dateLabels,
                datasets: [
                    { label: 'AI Solutions', data: aiDailyCosts.map(d => d.cost), borderColor: '#6495ED', tension: 0.4, fill: false, pointRadius: 0, borderWidth: 2 },
                    { label: 'Data Solutions', data: dataDailyCosts.map(d => d.cost), borderColor: '#FFB74D', tension: 0.4, fill: false, pointRadius: 0, borderWidth: 2 },
                ]
            });
            setLoadingNonK8s(false);
        }, 300);
    }
}, [resourceType, dateRange]);
 
 // --- NEW --- Effects to calculate overall cost for active non-k8s toggles
 useEffect(() => {
     if (processedAiSolutionsData[activeAiSolution]) {
         const activeServices = processedAiSolutionsData[activeAiSolution];
         const total = activeServices.reduce((sum, s) => sum + s.totalCost, 0);
         const change = activeServices.reduce((sum, s) => sum + s.costChange, 0);
         setOverallAiSolutionCost({ total, change });
     }
 }, [activeAiSolution, processedAiSolutionsData]);
 
 useEffect(() => {
     if (processedDataSolutionsData[activeDataSolution]) {
         const activeServices = processedDataSolutionsData[activeDataSolution];
         const total = activeServices.reduce((sum, s) => sum + s.totalCost, 0);
         const change = activeServices.reduce((sum, s) => sum + s.costChange, 0);
         setOverallDataSolutionCost({ total, change });
     }
 }, [activeDataSolution, processedDataSolutionsData]);
/**
 * Handles clicks on predefined date filter buttons.
 * @param filterType - The type of filter clicked (e.g., 'yesterday', 'last7days').
 * @param days - Number of days for the filter, or 'yesterday'.
 */
// const handleDateFilterClick = (filterType: string, days?: number | 'yesterday') => {
//     setActiveDateFilter(filterType);
//     const today = new Date();
//     let start: Date;
//     let end: Date = endOfDay(today); // End date is usually today (or yesterday for 'yesterday' filter)
//     if (days === 'yesterday') {
//         start = startOfDay(subDays(today, 1));
//         end = endOfDay(subDays(today, 1));
//     } else if (typeof days === 'number') {
//         start = startOfDay(subDays(today, days - 1)); // e.g., for 7 days, go back 6 days from today
//     } else { // Default case (should ideally not be hit if filterType is specific)
//         start = startOfDay(subDays(today, 29)); // Default to last 30 days
//     }
//     setDateRange({ startDate: start, endDate: end });
// };

const handleDateFilterClick = (filterType: string, days?: number | 'yesterday') => {
    setActiveDateFilter(filterType);
    const today = new Date(); // The real 'today'

    // Conditionally set the end-point for date calculations.
    // For real data, it's today. For mock data, it's yesterday.
    const referenceDate = USE_MOCK_DATA ? subDays(today, 1) : today;

    let start: Date;
    let end: Date = endOfDay(referenceDate);

    if (days === 'yesterday') {
        // "Yesterday" is a special case and is the same for both mock and real data.
        // It always refers to the actual previous day, so we use 'today' as the base.
        const yesterday = subDays(today, 1);
        start = startOfDay(yesterday);
        end = endOfDay(yesterday);
    } else if (typeof days === 'number') {
        // For ranges like "Last 7/30 days", we calculate backwards from our conditional reference date.
        start = startOfDay(subDays(referenceDate, days - 1));
    } else {
        // Fallback case also uses the reference date.
        start = startOfDay(subDays(referenceDate, 29));
    }

    setDateRange({ startDate: start, endDate: end });
};

/**
 * Handles changes from the custom date range picker.
 * @param dates - Can be a tuple [start, end], a single Date, or null.
 */
// const handleCustomDateChange = (dates: [Date | null, Date | null] | Date | null) => {
//     if (Array.isArray(dates)) { // Range selected
//         const [start, end] = dates;
//         let finalStartDate = start ? startOfDay(start) : null;
//         let finalEndDate = end ? endOfDay(end) : null;

//         // If start is after end, swap them
//         if (finalStartDate && finalEndDate && finalStartDate > finalEndDate) {
//             [finalStartDate, finalEndDate] = [finalEndDate, finalStartDate];
//         }
//         setDateRange({ startDate: finalStartDate, endDate: finalEndDate });
//         // Set filter to 'custom' if a start date is picked
//         if (finalStartDate) { setActiveDateFilter('custom'); }
//         else { handleDateFilterClick('last30days', 30); } // Revert to default if start date cleared
//     } else if (dates) { // Single date selected (treat as a one-day range)
//         const singleDate = startOfDay(dates);
//         setDateRange({startDate: singleDate, endDate: endOfDay(dates)});
//         setActiveDateFilter('custom');
//     } else { // Date picker cleared (though isClearable=false, defensive)
//         handleDateFilterClick('last30days', 30); // Revert to default
//     }
// };

// Cluster Dropdown Handlers
const handleClusterDropdownOpen = () => { setClusterSearchTerm(''); setStagedClusterSelection(selectedClusterNames); setIsClusterDropdownOpen(true); };
const handleClusterDropdownClose = () => { setIsClusterDropdownOpen(false); };
const handleStagedClusterSelectionChange = (clusterName: string) => { setStagedClusterSelection(prev => prev.includes(clusterName) ? prev.filter(name => name !== clusterName) : [...prev, clusterName]); };
const handleApplyClusterSelection = () => { setSelectedClusterNames(stagedClusterSelection); handleClusterDropdownClose(); };
const handleClusterSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => { setClusterSearchTerm(event.target.value); };

// Application Dropdown Handlers
const handleApplicationDropdownOpen = () => { setApplicationSearchTerm(''); setStagedApplicationSelection(selectedApplicationNames); setIsApplicationDropdownOpen(true); };
const handleApplicationDropdownClose = () => { setIsApplicationDropdownOpen(false); };
const handleStagedApplicationSelectionChange = (appName: string) => { setStagedApplicationSelection(prev => prev.includes(appName) ? prev.filter(name => name !== appName) : [...prev, appName]); };
const handleApplyApplicationSelection = () => { setSelectedApplicationNames(stagedApplicationSelection); handleApplicationDropdownClose(); };
const handleApplicationSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => { setApplicationSearchTerm(event.target.value); };

// Service Dropdown Handlers
const handleServiceDropdownOpen = () => { setServiceSearchTerm(''); setStagedServiceSelection(selectedServiceNames); setIsServiceDropdownOpen(true); };
const handleServiceDropdownClose = () => { setIsServiceDropdownOpen(false); };
const handleStagedServiceSelectionChange = (serviceName: string) => { setStagedServiceSelection(prev => prev.includes(serviceName) ? prev.filter(name => name !== serviceName) : [...prev, serviceName]); };
const handleApplyServiceSelection = () => { setSelectedServiceNames(stagedServiceSelection); handleServiceDropdownClose(); };
const handleServiceSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => { setServiceSearchTerm(event.target.value); };

// Memoized calculation for overall cluster cost (sum of costs for selected clusters)
const overallClusterCost = useMemo((): number => {
    if (!allClusterData || allClusterData.length === 0) return 0;
    let totalCost = 0;
    allClusterData.forEach(cluster => {
        if (selectedClustersSet.has(cluster.clusterName)) { // Only include selected clusters
            cluster.data.forEach(dataPoint => { totalCost += dataPoint.cost; });
        }
    });
    return totalCost;
}, [allClusterData, selectedClustersSet]);

// Memoized list of available clusters filtered by search term
const filteredAvailableClusters = useMemo(() =>
    availableClusters.filter(name => name.toLowerCase().includes(clusterSearchTerm.toLowerCase())),
    [availableClusters, clusterSearchTerm]
);
// Memoized list of available applications filtered by search term
const filteredAvailableApplications = useMemo(() =>
    availableApplications.filter(name => name.toLowerCase().includes(applicationSearchTerm.toLowerCase())),
    [availableApplications, applicationSearchTerm]
);

const filteredAvailableServices = useMemo(() =>
    availableServices.filter(name => name.toLowerCase().includes(serviceSearchTerm.toLowerCase())),
    [availableServices, serviceSearchTerm]
  );

// Memoized text describing the current date period (e.g., "Last 7 Days", "Yesterday", "MMM d - MMM d, yyyy")
const datePeriodText = useMemo(() => {
    if (!dateRange.startDate) return "Selected Period"; // Fallback
    const currentYear = new Date().getFullYear();
    const startYear = dateRange.startDate.getFullYear();

    switch (activeDateFilter) {
        case 'yesterday': return 'Yesterday';
        case 'last7days': return 'Last 7 Days';
        case 'last30days': return 'Last 30 Days';
        case 'custom':
            if (dateRange.startDate && dateRange.endDate) {
                // Single day selected
                if (format(dateRange.startDate, 'yyyy-MM-dd') === format(dateRange.endDate, 'yyyy-MM-dd')) {
                    return startYear === currentYear ? format(dateRange.startDate, 'MMM d') : format(dateRange.startDate, 'MMM d, yyyy');
                }
                // Date range
                const startFormat = startYear === dateRange.endDate.getFullYear() ? 'MMM d' : 'MMM d, yyyy';
                return `${format(dateRange.startDate, startFormat)} - ${format(dateRange.endDate, 'MMM d, yyyy')}`;
            }
            // Only start date selected in custom range
            return dateRange.startDate ? `${format(dateRange.startDate, 'MMM d, yyyy')}...` : "Custom";
        default: return "Selected Period"; // Generic fallback
    }
}, [activeDateFilter, dateRange]);

// Memoized chart data for the cluster cost overview line chart
const clusterChartData = useMemo((): ChartData<'line'> => {
    const allDates = new Set<string>(); // Collect all unique dates from selected cluster data
    const relevantClusterData = allClusterData.filter(cluster => selectedClustersSet.has(cluster.clusterName));
    relevantClusterData.forEach(cluster => { cluster.data.forEach(dp => allDates.add(dp.date)); });

    const sortedLabels = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()); // Sort dates chronologically

    const datasets = relevantClusterData.map(cluster => {
        const costMap = new Map(cluster.data.map(dp => [dp.date, dp.cost])); // Map date to cost for this cluster
        const color = clusterChartColors[cluster.clusterName] || '#000000'; // Get assigned color or default
        return {
            label: cluster.clusterName,
            data: sortedLabels.map(date => costMap.get(date) ?? null), // Cost for each date, or null if no data
            borderColor: color,
            backgroundColor: color + '33', // Lighter version of border color for area fill (if used)
            fill: false, // No area fill under the line
            tension: 0.4, // Line smoothing
            pointRadius: 0, // No points on the line
            pointHoverRadius: 5, // Radius of point on hover
            pointHitRadius: 10, // Hit detection radius for tooltips
            borderWidth: 2,
            spanGaps: true, // Connect line across null data points
        };
    });
    return { labels: sortedLabels, datasets: datasets };
}, [allClusterData, selectedClustersSet, clusterChartColors]);

// Memoized chart options for the cluster cost overview line chart
const clusterChartOptions = useMemo((): ChartOptions<'line'> => ({
    responsive: true,
    maintainAspectRatio: false, // Allow chart to fill container height
    plugins: {
        legend: {
            display: true,
            position: 'bottom',
            align: 'start',
            labels: {
                boxWidth: 12,
                padding: 20,
                font: { size: 12, family:"Gilroy-SemiBold" },
                usePointStyle: true, // Use point style (circle) for legend items
                pointStyle: 'circle',
                generateLabels: (chart) => { // Customize legend labels
                    const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                    originalLabels.forEach(label => {
                        label.fillStyle = label.strokeStyle; // Match fill color to line color
                        label.pointStyle = 'circle';
                    });
                    return originalLabels;
                }
            },
            onClick: () => {} // Disable default legend click behavior (hiding datasets)
        },
        title: { display: false }, // No chart title
        tooltip: {
            enabled: false, // Disable default Chart.js tooltip
            external: CustomHtmlLineTooltip, // Use custom HTML tooltip
            mode: 'index', // Show tooltip for all datasets at the hovered index
            intersect: false, // Tooltip triggers even if not directly hovering over a point
            position: 'nearest' // Position tooltip near the caret
        },
    },
    scales: {
        x: {
            type: 'time',
            time: {
                unit: 'day',
                parser: 'yyyy-MM-dd', // How input dates are parsed
                tooltipFormat: 'PPP', // Format for dates in tooltip (e.g., "May 8, 2025")
                displayFormats: { day: 'MMM d' } // Format for dates on the x-axis
            },
            title: { display: false },
            grid: { display: false }, // No grid lines for x-axis
            ticks: { color: 'grey.600', font: { size: 12, family: 'Gilroy-Regular' } }
        },
        y: {
            beginAtZero: true, // Y-axis starts at 0
            title: { display: false },
            grid: { display: false, }, // No grid lines for y-axis
            ticks: {
                color: 'grey.600',
                font: { size: 12, family: 'Gilroy-Regular' },
                callback: function(value: string | number) { // Custom tick formatting (e.g., 1K, 1M)
                    if (typeof value === 'number') {
                        if (value >= 1000000) { return (value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1) + 'M'; }
                        if (value >= 1000) { return (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1) + 'K'; }
                        return formatCurrencyWithSmallValues(value); // Use detailed formatter for smaller values
                    }
                    return value;
                }
            }
        },
    },
    interaction: {
        mode: 'index', // Interactions (hover, tooltip) consider all items at the same x-index
        intersect: false, // Trigger interaction even if not directly over an element
        axis: 'x' // Interaction primarily along the x-axis
    },
    animation: { duration: 0 }, // No animations
    hover: {
        mode: 'index',
        intersect: false,
        axis: 'x'
    },
}), []); // Empty dependency array as options are static

/**
 * Handles mouse enter event on a cost trend bar for the application cost overview.
 * Sets up the content and anchor for the Popper tooltip.
 * @param event - The mouse event.
 * @param dateStr - The date string for the bar ('yyyy-MM-dd').
 * @param cost - The cost value for the bar.
 */
const handleBarMouseEnter = (event: React.MouseEvent<HTMLDivElement>, dateStr: string, cost: number) => {
    if (event.currentTarget) {
        const formattedDate = format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'MMM d');
        const formattedCost = formatCurrencyForPopperTooltip(cost); // Use the new formatter

        setBarTooltip({
            visible: true,
            content: { date: formattedDate, cost: formattedCost },
            anchorElement: event.currentTarget,
        });
    }
};

/**
 * Handles mouse leave event from a cost trend bar, hiding the Popper tooltip.
 */
const handleBarMouseLeave = () => {
    setBarTooltip({ visible: false, content: null, anchorElement: null }); // Hide tooltip and clear content/anchor
};
const [stagedDateRange, setStagedDateRange] = useState<DateRange>(dateRange);
useEffect(() => {
    setStagedDateRange(dateRange);
}, [dateRange]);

const handleClearCustomDate = () => {
    setStagedDateRange({ startDate: null, endDate: null });
};
const handleApplyCustomDate = () => {
    // Apply the staged dates to the main state
    if (stagedDateRange.startDate) {
        setDateRange(stagedDateRange);
        setActiveDateFilter('custom');
    }
    // Close the calendar
    datePickerRef.current?.setOpen(false);
};
const handleCustomDateChange = (dates: [Date | null, Date | null] | Date | null) => {
    if (Array.isArray(dates)) {
        const [start, end] = dates;
        setStagedDateRange({ startDate: start, endDate: end });
    } else { // Single date click
        setStagedDateRange({ startDate: dates, endDate: null });
    }
};
interface CalendarFooterProps {
  onClear: () => void;
  onApply: () => void;
}
const CalendarFooter: React.FC<CalendarFooterProps> = ({ onClear, onApply }) => (
  <Box sx={{ display: 'flex', alignItems:'right', gap: 1.4, p: 1, mt: 1, ml:17 }}>
    <Button variant="text" onClick={onClear} sx={{ fontFamily: 'Gilroy-Bold', color: 'text.primary' }}>
      Clear
    </Button>
    <Button variant="text" onClick={onApply} sx={{ fontFamily: 'Gilroy-Bold', color: 'text.primary' }}>
      Apply
    </Button>
  </Box>
);
const CalendarHeader = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: {
  monthDate: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0 8px 16px 8px', // Adjust padding as needed
    }}
  >
    <Typography sx={{ fontFamily: '"Gilroy-Bold", sans-serif', fontSize: '1rem', textTransform: 'uppercase', color: '#212121' }}>
      {format(monthDate, 'MMMM yyyy')}
    </Typography>
    <Box>
      <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled} size="small">
        <ArrowBackIosNewIcon fontSize="inherit"  />
      </IconButton>
      <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled} size="small">
        <ArrowBackIosNewIcon fontSize="inherit" sx={{ ml:"24px", transform: 'rotate(180deg)' }}/>
      </IconButton>
    </Box>
  </Box>
);
const formatWeekdayName = (nameOfDay: string) => {
  return nameOfDay.substring(0, 3).toUpperCase();
};

return (
<Container disableGutters maxWidth={false} sx={{ padding: "24px", bgcolor: "#f9fafb", minHeight: "100vh" , overflowX:'hidden'}}>
    <MiniBot/> {/* Assuming MiniBot is a chat or helper component */}
<Box sx={{ width: '100%' }}> {/* Main content box */}
{/* Breadcrumbs and Page Title */}
<Box sx={{ display: 'flex', flexDirection: 'column', mb: 1}}>
    {/* <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "grey.600", mb: 1, fontFamily: "Gilroy-Regular" }}>
        <Link underline="hover" color="inherit" href="#" onClick={(event) => { event.preventDefault(); navigate("/dashboard"); }} sx={{fontFamily: "Gilroy-Regular"}}>
            Dashboard
        </Link>
        <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: "Gilroy-SemiBold" }}>
            FinOps Dashboard
        </Typography>
    </Breadcrumbs> */}
    <Box display="flex" justifyContent="space-between">
    <Box display="flex" alignItems="center"  flexDirection="row">
        {/* <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/dashboard")} sx={{ mr: 1, p: 0.5 }}>
            <ArrowBackIosNewIcon fontSize="small" />
        </IconButton> */}
        <Typography fontFamily="Gilroy-Bold" fontSize="22px"> FinOps Dashboard </Typography>
        
    </Box>
     <IconButton
                                          aria-label="Help" 
                                          onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                          sx={{
                                            padding: 0, 
                                            border: 'none',  
                                            ml:2,
                                            '&:hover': {
                                              
                                              backgroundColor: 'action.hover', 
                                            },
                                            
                                          }}
                                        >
                                          <Box
                                            component="img"
                                            src="/assets/Help.svg" 
                                            alt="" 
                                            sx={{
                                              width: 40,  
                                              height: 40,
                                              display: 'block', 
                                            }}
                                          />
                                        </IconButton>
    </Box>
</Box>
  <Divider sx={{ marginY: 1 }} />
<Box sx={{ p: { xs: 0, sm: 0, md: 0 }, marginTop:2}}> {/* Padding adjustments for different screen sizes */}
    {/* --- MODIFIED --- Filter Controls Wrapper */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',gap: 3, flexWrap: 'wrap', mb: 3 }}>
        {/* Date Filter Buttons and Custom Date Picker */}
        <Box sx={{ display: 'inline-flex', alignItems: 'stretch', height: '38px', bgcolor: 'transparent', borderRadius: '6px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
            <Stack direction="row" spacing={0} alignItems="stretch" height="100%">
                <Button sx={activeDateFilter === 'yesterday' ? dateFilterActiveStyle : dateFilterInactiveStyle} onClick={() => handleDateFilterClick('yesterday', 'yesterday')} variant="text"> Yesterday </Button>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />
                <Button sx={activeDateFilter === 'last7days' ? dateFilterActiveStyle : dateFilterInactiveStyle} onClick={() => handleDateFilterClick('last7days', 7)} variant="text"> Last 7 days </Button>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />
                <Button sx={activeDateFilter === 'last30days' ? dateFilterActiveStyle : dateFilterInactiveStyle} onClick={() => handleDateFilterClick('last30days', 30)} variant="text"> Last 30 Days </Button>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: '#dee2e6' }} />
                <StyledDatePickerWrapper>
                    <DatePicker
                        ref={datePickerRef}
                        selectsRange={true}
                        startDate={stagedDateRange.startDate}
                        endDate={stagedDateRange.endDate}
                        onChange={handleCustomDateChange}
                        shouldCloseOnSelect={false}
                        
                        // ----- ADD THE NEW PROPS HERE -----
                        locale={enIN} // Starts the week on Monday
                        formatWeekDay={formatWeekdayName}
                        renderCustomHeader={({
                            monthDate,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                        }) => (
                            <CalendarHeader
                                monthDate={monthDate}
                                decreaseMonth={decreaseMonth}
                                increaseMonth={increaseMonth}
                                prevMonthButtonDisabled={prevMonthButtonDisabled}
                                nextMonthButtonDisabled={nextMonthButtonDisabled}
                            />
                        )}
                        // ------------------------------------

                        customInput={
                            <CustomDatePickerInput
                                isActive={activeDateFilter === 'custom'}
                                value={
                                    activeDateFilter === 'custom' && dateRange.startDate ? (
                                    dateRange.endDate ? (
                                        format(dateRange.startDate, 'yyyy-MM-dd') === format(dateRange.endDate, 'yyyy-MM-dd')
                                        ? format(dateRange.startDate, 'MMM d, yy')
                                        : `${format(dateRange.startDate, 'MMM d')} - ${format(dateRange.endDate, 'MMM d, yy')}`
                                    ) : `${format(dateRange.startDate, 'MMM d, yy')}...`
                                    ) : undefined
                                }
                            />
                        }
                        popperPlacement="bottom-start"
                    >
                        <CalendarFooter
                            onClear={handleClearCustomDate}
                            onApply={handleApplyCustomDate}
                            
                        />
                    </DatePicker>
                </StyledDatePickerWrapper>
            </Stack>
        </Box>

        {/* --- NEW --- K8s / Non-K8s Toggle */}
        {/* K8s / Non-K8s Toggle */}
<Box sx={{ display: 'inline-flex', alignItems: 'center', p: '4px', bgcolor: '#F0F2F5', borderRadius: '8px' }}>
     <Button sx={resourceType === 'k8s' ? toggleActiveStyle : toggleInactiveStyle} onClick={() => setResourceType('k8s')}> K8s Resources </Button>
     <Button sx={resourceType === 'non-k8s' ? toggleActiveStyle : toggleInactiveStyle} onClick={() => setResourceType('non-k8s')}> Non K8s Resources </Button>
</Box>
    </Box>

    {/* Cost Recommendations Banner */}
    <Paper elevation={0} sx={{ mb: 3, p: 2, display: 'flex', alignItems: 'center', gap: 2, backgroundImage:'url("/assets/cost-dashboard-recommendation.svg")', backgroundRepeat: 'no-repeat', backgroundSize:'cover', borderRadius: "8px" }}>
        <img src="/assets/quill-pen-ai-fill.svg" alt="Recommendation Logo" style={{ width: '35px', height: '35px', display: 'block' }} />
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{fontFamily: "Gilroy-Bold"}}>Cost Recommendations</Typography>
            <Typography variant="body2" color="text.secondary" sx={{fontFamily: "Gilroy-Regular", marginRight:"20vw"}}>
                Our analysis indicates a significant increase in your costs. Based on in-depth analysis of your data, here are tailored recommendations to help optimize your cost usage.
            </Typography>
        </Box>
        <Box sx={{ p: '2px', borderRadius: '5px', display: 'inline-block', flexShrink: 0 }}>
        <img 
            src="/assets/coming-soon-cost-1.svg" 
            alt="stars" 
            style={{ 
                width: '18px', 
                height: '18px',
                marginBottom:'1.5vh',
            }} 
            />

        <GradientText>
            Coming Soon
            </GradientText>
            <img 
            src="/assets/coming-soon-cost-2.svg" 
            alt="stars" 
            style={{ 
                width: '10px', 
                height: '10px',
                marginBottom:'2vh',
                marginRight:'2vw',
                marginLeft:'-2.5vw'
            }} 
            />

        </Box>
    </Paper>

    {/* --- NEW --- Conditional Rendering based on Resource Type */}
    {resourceType === 'k8s' ? (
        <>
            {/* Cluster Cost Overview Section */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', mb: 3 }}>
                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} rowSpacing={{xs: 1.5, md:0}}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{fontFamily: "Gilroy-Bold"}}>Cost Overview</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily:"Gilroy-Regular" }}>
                            {selectedClusterNames.length > 0 ? `Showing cost for ${selectedClusterNames.length} selected cluster${selectedClusterNames.length ===1 ? '':'s'}` : 'Select clusters to view cost'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} container justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        {/* Cluster Selection Dropdown */}
                        <FormControl sx={{ minWidth: 200, maxWidth: 300 }} size="small">
                            <Select
                                multiple
                                displayEmpty
                                value={selectedClusterNames}
                                onChange={() => { /* Selection handled by MenuItem clicks */}}
                                input={<OutlinedInput notched={false} />}
                                open={isClusterDropdownOpen}
                                onOpen={handleClusterDropdownOpen}
                                onClose={handleClusterDropdownClose}
                                IconComponent={KeyboardArrowDownIcon}
                                sx={{ ...generalOutlinedButtonStyle, minWidth: 200, maxWidth: {xs: '100%', sm: 300}, '& .MuiSelect-select': { py: '7.5px', px: 1.5, textAlign: 'left', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block' }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                                renderValue={(selected) => { // Custom rendering of selected values
                                    if (selected.length === 0) { return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>Select Clusters</Typography>; }
                                    const displayCount = 1; // Max number of names to show before "..."
                                    const displayedNames = selected.slice(0, displayCount).join(', ');
                                    let displayText = `Clusters: ${displayedNames}`;
                                    if (selected.length > displayCount) { displayText += `, ... (+${selected.length - displayCount})`; }
                                    return ( <Typography noWrap title={selected.join(', ')} sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'black' }}> {displayText} </Typography> );
                                }}
                                MenuProps={{ autoFocus: false, PaperProps: { sx: { width: {xs: 280, sm:300}, maxHeight: 400 } }, }}
                            >
                                <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}>
                                    <TextField /* Search field within dropdown */
                                        size="small" fullWidth placeholder="Search by cluster name"
                                        value={clusterSearchTerm} onChange={handleClusterSearchChange}
                                        onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} // Prevent dropdown close on click/keydown
                                        InputProps={{
                                            startAdornment: ( <InputAdornment position="start" sx={{ pl: '12px', pr: '8px', mt: '0 !important' }}> <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", display: 'block' }} /> </InputAdornment> ),
                                            endAdornment: clusterSearchTerm ? ( <InputAdornment position="end"> <IconButton size="small" onClick={(e) => { e.stopPropagation(); setClusterSearchTerm(''); }}><CloseIcon fontSize="small"/></IconButton> </InputAdornment> ) : null,
                                            sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily:'Gilroy-Regular' }
                                        }}
                                        variant="outlined" autoFocus
                                    />
                                </ListSubheader>
                                <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt:0.5 }}> {/* Scrollable area for cluster items */}
                                    {filteredAvailableClusters.map((clusterName) => (
                                        <MenuItem key={clusterName} value={clusterName} onClick={() => { handleStagedClusterSelectionChange(clusterName); }} sx={{ py: 0.3, borderRadius:'4px', m:0.5, '&:hover': {backgroundColor: 'rgba(0,0,0,0.04)'} }} >
                                            <Checkbox checked={stagedClusterSelection.includes(clusterName)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} />
                                            <ListItemText primary={clusterName} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily:'Gilroy-Regular' } }} />
                                        </MenuItem>
                                    ))}
                                    {/* Messages for no matches or no clusters */}
                                    {filteredAvailableClusters.length === 0 && clusterSearchTerm && ( <MenuItem disabled sx={{justifyContent: 'center', py:2}}> <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular'}}>No matches found</Typography> </MenuItem> )}
                                    {availableClusters.length === 0 && !clusterSearchTerm && ( <MenuItem disabled sx={{justifyContent: 'center', py:2}}> <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular'}}>No clusters available</Typography> </MenuItem> )}
                                </Box>
                                <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}> {/* Apply button and selection count */}
                                    <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular', fontSize:'11px'}}> {stagedClusterSelection.length} Selected </Typography>
                                    <Button size="small" variant="contained" onClick={handleApplyClusterSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }} > Apply </Button>
                                </Box>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                {/* INSERT NEW OVERALL CLUSTER COST CARD HERE */}
                {!loadingClusters && !errorClusters && overallTotalCostFromApi !== null && dateRange.startDate && (
                            <Paper
                                elevation={0} // Or a small elevation like 1 if you want a slight shadow for the block
                                sx={{
                                    py:0.5,
                                    px:2,
                                    mb: 2, // Margin below this card, before the chart
                                    display: 'inline-flex', // Makes it a block that only takes up necessary width, plus padding
                                    flexDirection: 'column', // Aligns content vertically if needed, for text wrapping
                                    alignItems: 'flex-start', // Aligns content to the start
                                    borderLeft: '6px solid',
                                    borderColor: overallCostDifferenceFromApi !== null && overallCostDifferenceFromApi >= 0 ? '#D92D20' : '#039855', // Darker red/green for the border
                                    backgroundColor: overallCostDifferenceFromApi !== null && overallCostDifferenceFromApi >= 0 ? '#FEF3F2' : '#ECFDF3', // Light red for positive/zero, Light green for negative
                                    borderRadius: '4px',
                                    minWidth: '200px', // Optional: ensure it has a minimum width
                                    // maxWidth: 'fit-content', // Ensures it doesn't stretch unnecessarily if parent is flex
                                }}
                            >
                                <Box sx={{ flexGrow: 1, width: '100%' /* Ensures inner content can use full width of this card */ }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Regular", display: 'inline-block', mr: 1, fontSize:"14px" }}>
                                        Overall Cost:
                                    </Typography>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", display: 'inline-block', mr: 1, fontSize:"14px" }}>
                                        {formatCurrencyWithSmallValues(overallTotalCostFromApi)}
                                    </Typography>
                                    {overallCostDifferenceFromApi !== null && (
                                        <Chip
                                            icon={
                                                overallCostDifferenceFromApi === 0 ? <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ color: "text.secondary" }}>–</Typography>
                                                : overallCostDifferenceFromApi > 0 ?
                                                <img src={"/assets/arrow-up-red.svg"} alt="Up" style={{ width: '16px', height: '16px' }} />
                                                : <img src={"/assets/arrow-down-green.svg"} alt="Down" style={{ width: '16px', height: '16px' }} />
                                            }
                                            size="small"
                                            sx={{
                                                height: '22px',
                                                fontSize: '0.75rem',
                                                fontFamily: 'Gilroy-Medium',
                                                backgroundColor: 'transparent',
                                                color: overallCostDifferenceFromApi === 0 ? 'text.primary' : overallCostDifferenceFromApi > 0 ? '#D92D20' : '#039855', // Text/icon color matches border
                                                '.MuiChip-label': { px: '4px' },
                                                '.MuiChip-icon': { ml: '0px', mr: '0px', color: 'inherit' },
                                                border: 'none',
                                                verticalAlign: 'middle'
                                            }}
                                        />
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", mt: 0.5 /* Added a little margin top */ }}>
                                        {`In ${datePeriodText.toLowerCase()}`}
                                    </Typography>
                                </Box>
                            </Paper>
                        )}
                        {/* END OF NEW OVERALL CLUSTER COST CARD */}

                {/* Cluster Cost Chart Area */}
                <Box sx={{ position: 'relative', height: 350, width: '100%', mt: 2 }}>
                    {loadingClusters && ( /* Loading spinner */
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily:"Gilroy-Regular" }}>Loading chart data...</Typography>
                        </Stack>
                    )}
                    {errorClusters && ( /* Error message */
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', p: 2 }}>
                            <Alert severity="error" sx={{width: 'auto', fontFamily:"Gilroy-Regular"}}>Error loading data: {errorClusters}</Alert>
                        </Stack>
                    )}
                    {/* Chart rendering conditions */}
                    {!loadingClusters && !errorClusters && dateRange.startDate && dateRange.endDate && clusterChartData.datasets.length > 0 && selectedClusterNames.length > 0 && (
                        <Line options={clusterChartOptions} data={clusterChartData} />
                    )}
                    {/* Message if end date is missing */}
                    {!loadingClusters && !errorClusters && dateRange.startDate && !dateRange.endDate && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                            <Typography variant="body1" color="text.secondary" sx={{fontFamily:"Gilroy-Regular"}}> Please select an end date. </Typography>
                        </Stack>
                    )}
                    {/* Message if no clusters are selected */}
                    {!loadingClusters && !errorClusters && availableClusters.length > 0 && selectedClusterNames.length === 0 && dateRange.startDate && dateRange.endDate && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                            <Typography variant="body1" color="text.secondary" sx={{fontFamily:"Gilroy-Regular"}}> Select clusters above to display cost data. </Typography>
                        </Stack>
                    )}
                    {/* Message if no cluster data is available for the timeframe */}
                    {!loadingClusters && !errorClusters && availableClusters.length === 0 && dateRange.startDate && dateRange.endDate && allClusterData.length === 0 && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                            <Typography variant="body1" color="text.secondary" sx={{fontFamily:"Gilroy-Regular"}}> No cluster cost data available for the selected timeframe. </Typography>
                        </Stack>
                    )}
                </Box>
            </Paper>

            {/* Application Cost Overview Section */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', mt: 3 }}>
                <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }} rowSpacing={{ xs: 1.5, md: 0 }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Application Cost Overview</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily: "Gilroy-Regular" }}>
                            {selectedClusterNames.length > 0 ? `Showing for ${datePeriodText}.` : 'Please select cluster(s) first.'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} container justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        {/* Application Selection Dropdown: Shown if clusters are selected and applications are available */}
                        {selectedClusterNames.length > 0 && !loadingClusters && availableApplications.length > 0 && (
                            <FormControl sx={{ minWidth: 200, maxWidth: 300 }} size="small">
                                <Select
                                    multiple
                                    displayEmpty
                                    value={selectedApplicationNames} // Currently selected applications
                                    onChange={() => { /* Selection handled by MenuItem clicks */ }}
                                    input={<OutlinedInput notched={false} />}
                                    open={isApplicationDropdownOpen}
                                    onOpen={handleApplicationDropdownOpen}
                                    onClose={handleApplicationDropdownClose}
                                    IconComponent={KeyboardArrowDownIcon}
                                    sx={{ ...generalOutlinedButtonStyle, minWidth: 200, maxWidth: { xs: '100%', sm: 300 }, '& .MuiSelect-select': { py: '7.5px', px: 1.5 }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                                    renderValue={(selected) => { // Custom display for selected applications
                                        if (selected.length === 0) { // No specific apps selected means "All Applications"
                                            return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>All Applications</Typography>;
                                        }
                                        // If all available applications (for selected clusters) are chosen
                                        if (selected.length === availableApplications.length) {
                                            return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'black' }}>All Applications ({selected.length})</Typography>;
                                        }
                                        const displayCount = 1; // Max names to show before "..."
                                        const displayedNames = selected.slice(0, displayCount).join(', ');
                                        let displayText = `Apps: ${displayedNames}`;
                                        if (selected.length > displayCount) {
                                            displayText += `, ... (+${selected.length - displayCount})`;
                                        }
                                        return ( <Typography noWrap title={selected.join(', ')} sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'black' }}> {displayText} </Typography> );
                                    }}
                                    MenuProps={{ autoFocus: false, PaperProps: { sx: { width: { xs: 280, sm: 300 }, maxHeight: 400 } }, }}
                                >
                                    <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}>
                                        <TextField /* Search field for applications */
                                            size="small" fullWidth placeholder="Search application name"
                                            value={applicationSearchTerm} onChange={handleApplicationSearchChange}
                                            onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
                                            InputProps={{
                                                startAdornment: ( <InputAdornment position="start" sx={{ pl: '12px', pr: '8px', mt: '0 !important' }}> <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", display: 'block' }} /> </InputAdornment> ),
                                                endAdornment: applicationSearchTerm ? (<InputAdornment position="end"> <IconButton size="small" onClick={(e) => { e.stopPropagation(); setApplicationSearchTerm(''); }}><CloseIcon fontSize="small" /></IconButton> </InputAdornment>) : null,
                                                sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily: 'Gilroy-Regular' }
                                            }}
                                            variant="outlined" autoFocus
                                        />
                                    </ListSubheader>
                                    <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt: 0.5 }}> {/* Scrollable list of applications */}
                                        {filteredAvailableApplications.map((appName) => (
                                            <MenuItem key={appName} value={appName} onClick={() => handleStagedApplicationSelectionChange(appName)} sx={{ py: 0.3, borderRadius: '4px', m: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                                                <Checkbox checked={stagedApplicationSelection.includes(appName)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} />
                                                <ListItemText primary={appName} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily: 'Gilroy-Regular', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                                            </MenuItem>
                                        ))}
                                        {/* Messages for search results or availability */}
                                        {filteredAvailableApplications.length === 0 && applicationSearchTerm && ( <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}> <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>No matches found</Typography> </MenuItem> )}
                                        {availableApplications.length > 0 && filteredAvailableApplications.length === 0 && !applicationSearchTerm && ( <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}> <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>All apps hidden by current filter/search</Typography> </MenuItem> )}
                                        {availableApplications.length === 0 && selectedClusterNames.length > 0 && !loadingApplications && !applicationSearchTerm && ( <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}> <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>No applications in selected cluster(s)</Typography> </MenuItem> )}
                                    </Box>
                                    <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}> {/* Apply button and selection count */}
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '11px' }}>
                                            {stagedApplicationSelection.length} Selected {stagedApplicationSelection.length === 0 && availableApplications.length > 0 ? `(All ${availableApplications.length} shown)` : ''}
                                        </Typography>
                                        <Button size="small" variant="contained" onClick={handleApplyApplicationSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }} > Apply </Button>
                                    </Box>
                                </Select>
                            </FormControl>
                        )}
                         {/* Message if no applications found for selected clusters */}
                          {selectedClusterNames.length > 0 && !loadingClusters && availableApplications.length === 0 && !loadingApplications && !errorApplications &&(
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontStyle: 'italic', mt:1, textAlign:'right', width:'100%' }}>No applications found for selected cluster(s) in this period.</Typography>
                        )}
                    </Grid>
                </Grid>
                {!loadingApplications && !errorApplications && overallApplicationTotalCostFromApi !== null && dateRange.startDate && selectedClusterNames.length > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                            py:0.5, px:2, mb: 2, display: 'inline-flex', flexDirection: 'column',
                            alignItems: 'flex-start', borderLeft: '6px solid',
                            borderColor: overallApplicationCostDifferenceFromApi !== null && overallApplicationCostDifferenceFromApi >= 0 ? '#D92D20' : '#039855',
                            backgroundColor: overallApplicationCostDifferenceFromApi !== null && overallApplicationCostDifferenceFromApi >= 0 ? '#FEF3F2' : '#ECFDF3',
                            borderRadius: '4px', minWidth: '200px',
                        }}
                    >
                        <Box sx={{ flexGrow: 1, width: '100%' }}>
                            <Typography sx={{ fontFamily: "Gilroy-Regular", display: 'inline-block', mr: 1, fontSize:"14px" }}>
                                Overall Application Cost:
                            </Typography>
                            <Typography sx={{ fontFamily: "Gilroy-Bold", display: 'inline-block', mr: 1, fontSize:"14px" }}>
                                {formatCurrencyWithSmallValues(overallApplicationTotalCostFromApi)}
                            </Typography>
                            {overallApplicationCostDifferenceFromApi !== null && (
                                <Chip
                                    icon={
                                        overallApplicationCostDifferenceFromApi === 0 ? <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ color: "text.secondary" }}>–</Typography>
                                        : overallApplicationCostDifferenceFromApi > 0 ?
                                        <img src={"/assets/arrow-up-red.svg"} alt="Up" style={{ width: '16px', height: '16px' }} />
                                        : <img src={"/assets/arrow-down-green.svg"} alt="Down" style={{ width: '16px', height: '16px' }} />
                                    }
                                    size="small"
                                    sx={{
                                        height: '22px', fontSize: '0.75rem', fontFamily: 'Gilroy-Medium',
                                        backgroundColor: 'transparent',
                                        color: overallApplicationCostDifferenceFromApi === 0 ? 'text.primary' : overallApplicationCostDifferenceFromApi > 0 ? '#D92D20' : '#039855',
                                        '.MuiChip-label': { px: '4px' }, '.MuiChip-icon': { ml: '0px', mr: '0px', color: 'inherit' },
                                        border: 'none', verticalAlign: 'middle'
                                    }}
                                />
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", mt: 0.5 }}>
                                {`In ${datePeriodText.toLowerCase()}`}
                            </Typography>
                        </Box>
                    </Paper>
                )}
                {/* Application Data Table/List Headers: Shown if data is ready */}
                {selectedClusterNames.length > 0 && !loadingApplications && !errorApplications && processedApplicationData.length > 0 && dateRange.startDate && (
                    <Box sx={{ display: 'flex', py: 1, borderBottom: 1, borderColor: 'divider', mb: 0, backgroundColor: 'grey.50', pr: { xs: 1, sm: 2 }, pl: { xs: 1, sm: 2 } }}>
                        <Typography variant="caption" sx={{ flex: { xs: '1 1 40%', sm: '0 0 40%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', pr: 1 }}>APPLICATION NAME</Typography>
                        <Typography variant="caption" sx={{ flex: { xs: '1 1 30%', sm: '0 0 25%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', textAlign: 'right', pr: 1 }}>TOTAL COST ({datePeriodText})</Typography>
                        <Typography variant="caption" sx={{ flex: { xs: '1 1 30%', sm: '0 0 35%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', textAlign: 'center' }}>COST TREND ({datePeriodText})</Typography>
                    </Box>
                )}

                {/* Loading state for applications */}
                {loadingApplications && selectedClusterNames.length > 0 && (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: "Gilroy-Regular" }}>Loading application data...</Typography>
                    </Stack>
                )}
                {/* Error state for applications */}
                {errorApplications && selectedClusterNames.length > 0 && (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                        <Alert severity="error" sx={{ width: 'auto', fontFamily: "Gilroy-Regular" }}>Error: {errorApplications}</Alert>
                    </Stack>
                )}

                {/* Display processed application data */}
                {!loadingApplications && !errorApplications && selectedClusterNames.length > 0 && dateRange.startDate && (
                    processedApplicationData.length > 0 ? (
                        processedApplicationData.map(clusterApp => ( // Iterate over clusters
                            <Box key={clusterApp.clusterName} sx={{ mb: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', p: 1, pl: { xs: 1, sm: 2 }, backgroundColor: 'grey.100', borderBottom: '1px solid #eee', fontFamily: 'Gilroy-Bold' }}>
                                    {clusterApp.clusterName} {/* Cluster Name Header */}
                                </Typography>
                                {clusterApp.applications.length === 0 && ( // Message if no apps for this cluster after filtering
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', fontFamily: 'Gilroy-Regular' }}>
                                        No applications match the current filter for this cluster.
                                    </Typography>
                                )}
                                {clusterApp.applications.map((app) => ( // Iterate over applications in this cluster
                                    <CostListItem
                                        key={app.appName}
                                        name={app.appName}
                                        totalCost={app.totalCost}
                                        costChange={app.costChange}
                                        costTrendData={app.costTrendData}
                                        maxCostInTrend={app.maxCostInTrend}
                                        dateRange={dateRange}
                                        onBarMouseEnter={handleBarMouseEnter}
                                        onBarMouseLeave={handleBarMouseLeave}
                                    />
                                ))}
                            </Box>
                        ))
                    ) : ( // Message if no application data to display after all filters
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 3, fontFamily: "Gilroy-Regular", p: 3 }}>
                            No application data available for the selected clusters and filters.
                        </Typography>
                    )
                )}
                {/* Message if no clusters are selected (for application section) */}
                {!loadingApplications && !errorApplications && selectedClusterNames.length === 0 && dateRange.startDate && (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 3, fontFamily: "Gilroy-Regular", p: 3 }}>
                        Please select cluster(s) to see application costs.
                    </Typography>
                )}
            </Paper>

            {/* SERVICE COST OVERVIEW SECTION */}
            <Box display="flex" sx={{ mt: 3, flexDirection: { xs: 'column', md: 'row' } }} gap={3}>
                    {/* Service Cost Overview Paper (Left 50%) */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', width: { xs: '100%', md: '50%' }, display: 'flex', flexDirection: 'column', maxHeight: '700px' }}>
                        <Grid container spacing={1} alignItems="center" sx={{ mb:2, flexShrink: 0 }} rowSpacing={{xs: 1.5, md:0}}>
                            <Grid item xs={12} sm={7} md={8}>
                                <Typography variant="h6" sx={{fontFamily: "Gilroy-Bold"}}>Service Cost Overview</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily:"Gilroy-Regular" }}>
                                    {/* Adjusted condition based on dependencies for service data */}
                                    {selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) ? `Showing for ${datePeriodText}.` : 'Select clusters & applications first.'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={5} md={4} container justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                                {/* Service Dropdown: Show if clusters and apps are selected/available, and services are available */}
                                {selectedClusterNames.length > 0 &&
                                 (selectedApplicationNames.length > 0 || availableApplications.length > 0) && // Context for apps exists
                                 !loadingClusters && !loadingApplications && // Ensure dependent data is loaded
                                 availableServices.length > 0 && !loadingServices && ( // Services are available and not loading
                                    <FormControl sx={{ minWidth: 180, width:'100%', maxWidth: {xs: '100%', sm: '100%'} }} size="small">
                                        <Select
                                            multiple
                                            displayEmpty
                                            value={selectedServiceNames}
                                            onChange={() => { /* Handled by MenuItem clicks */ }}
                                            input={<OutlinedInput notched={false} />}
                                            open={isServiceDropdownOpen}
                                            onOpen={handleServiceDropdownOpen}
                                            onClose={handleServiceDropdownClose}
                                            IconComponent={KeyboardArrowDownIcon}
                                            sx={{ ...generalOutlinedButtonStyle, minWidth: 180, width:'100%', '& .MuiSelect-select': { py: '7.5px', px: 1.5 }, '& .MuiSvgIcon-root': { color: 'black', right: 12 } }}
                                            renderValue={(selected) => {
                                                if (selected.length === 0) { return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'grey.700' }}>All Services</Typography>; }
                                                if (selected.length === availableServices.length) { return <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'black' }}>All Services ({selected.length})</Typography>; }
                                                const displayCount = 1; // Max names to show
                                                const displayedNames = selected.slice(0, displayCount).join(', ');
                                                let displayText = `${displayedNames}`;
                                                if (selected.length > displayCount) { displayText += `, ... (+${selected.length - displayCount})`;} // Corrected from snippet
                                                return ( <Typography noWrap title={selected.join(', ')} sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'black' }}> {displayText} </Typography> );
                                            }}
                                            MenuProps={{ autoFocus: false, PaperProps: { sx: { width: {xs: 280, sm:300}, maxHeight: 400 } }, }}
                                        >
                                            <ListSubheader sx={{ bgcolor: 'background.paper', p: 1, pt: 1.5, top: -8, borderBottom: '1px solid #eee' }}>
                                                <TextField
                                                    size="small" fullWidth placeholder="Search service name"
                                                    value={serviceSearchTerm} onChange={handleServiceSearchChange}
                                                    onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
                                                    InputProps={{
                                                        startAdornment: ( <InputAdornment position="start" sx={{ pl: '12px', pr: '8px', mt: '0 !important' }}> <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", display: 'block' }} /> </InputAdornment> ),
                                                        endAdornment: serviceSearchTerm ? (<InputAdornment position="end"> <IconButton size="small" onClick={(e) => { e.stopPropagation(); setServiceSearchTerm(''); }}><CloseIcon fontSize="small"/></IconButton> </InputAdornment>) : null,
                                                        sx: { fontSize: '0.875rem', borderRadius: '6px', fontFamily:'Gilroy-Regular'}
                                                    }}
                                                    variant="outlined" autoFocus
                                                />
                                            </ListSubheader>
                                            <Box sx={{ maxHeight: 250, overflow: 'auto', pl: 0.5, pr: 0.5, pt:0.5 }}>
                                                {filteredAvailableServices.map((serviceName) => (
                                                    <MenuItem key={serviceName} value={serviceName} onClick={() => handleStagedServiceSelectionChange(serviceName)} sx={{ py: 0.3, borderRadius:'4px', m:0.5, '&:hover': {backgroundColor: 'rgba(0,0,0,0.04)'} }}>
                                                        <Checkbox checked={stagedServiceSelection.includes(serviceName)} size="small" sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: 'black' } }} />
                                                        <ListItemText primary={serviceName} primaryTypographyProps={{ style: { fontSize: '0.875rem', fontFamily:'Gilroy-Regular', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                                                    </MenuItem>
                                                ))}
                                                {filteredAvailableServices.length === 0 && serviceSearchTerm && ( <MenuItem disabled sx={{justifyContent: 'center', py:2}}> <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular'}}>No matches found</Typography> </MenuItem> )}
                                                {availableServices.length > 0 && filteredAvailableServices.length === 0 && !serviceSearchTerm && ( <MenuItem disabled sx={{justifyContent: 'center', py:2}}> <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular'}}>All services hidden by search</Typography> </MenuItem> )}
                                                {/* Condition for no services available at all for the current cluster/app selection */}
                                                {availableServices.length === 0 && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) && !loadingServices && !loadingApplications && (
                                                    <MenuItem disabled sx={{justifyContent: 'center', py:2}}> <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular'}}>No services for current selection</Typography> </MenuItem>
                                                )}
                                            </Box>
                                            <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                                                <Typography variant="caption" color="text.secondary" sx={{fontFamily:'Gilroy-Regular', fontSize:'11px'}}>
                                                    {stagedServiceSelection.length} Selected {stagedServiceSelection.length === 0 && availableServices.length > 0 ? `(All ${availableServices.length} shown)` : ''}
                                                </Typography>
                                                <Button size="small" variant="contained" onClick={handleApplyServiceSelection} sx={{ ...generalContainedButtonStyle, height: 'auto', textTransform: 'uppercase', fontSize: '0.70rem', px: 1.5, py: 0.7 }} > Apply </Button>
                                            </Box>
                                        </Select>
                                    </FormControl>
                                )}
                                {/* Message if no services found for selected clusters/apps */}
                                 {selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) &&
                                 !loadingClusters && !loadingApplications && availableServices.length === 0 && !loadingServices && !errorServices && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', fontStyle: 'italic', mt:1, textAlign:'right', width:'100%' }}>No services found</Typography>
                                )}
                            </Grid>
                        </Grid>
                        {/* INSERT NEW OVERALL SERVICE COST CARD HERE */}
                        {!loadingServices && !errorServices && overallServiceTotalCostFromApi !== null && dateRange.startDate && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) && (
                            <Paper
                                elevation={0}
                                sx={{
                                    py:0.5, px:2, mb: 2, display: 'inline-flex', flexDirection: 'column',
                                    alignItems: 'flex-start', 
                                    alignSelf: 'flex-start',
                                    borderLeft: '6px solid',
                                    borderColor: overallServiceCostDifferenceFromApi !== null && overallServiceCostDifferenceFromApi >= 0 ? '#D92D20' : '#039855',
                                    backgroundColor: overallServiceCostDifferenceFromApi !== null && overallServiceCostDifferenceFromApi >= 0 ? '#FEF3F2' : '#ECFDF3',
                                    borderRadius: '4px', minWidth: '200px',
                                }}
                            >
                                <Box sx={{ flexGrow: 1, width: '100%' }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Regular", display: 'inline-block', mr: 1, fontSize:"14px" }}>
                                        Overall Service Cost:
                                    </Typography>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", display: 'inline-block', mr: 1, fontSize:"14px" }}>
                                        {formatCurrencyWithSmallValues(overallServiceTotalCostFromApi)}
                                    </Typography>
                                    {overallServiceCostDifferenceFromApi !== null && (
                                        <Chip
                                            icon={
                                                overallServiceCostDifferenceFromApi === 0 ? <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ color: "text.secondary" }}>–</Typography>
                                                : overallServiceCostDifferenceFromApi > 0 ?
                                                <img src={"/assets/arrow-up-red.svg"} alt="Up" style={{ width: '16px', height: '16px' }} />
                                                : <img src={"/assets/arrow-down-green.svg"} alt="Down" style={{ width: '16px', height: '16px' }} />
                                            }
                                            size="small"
                                            sx={{
                                                height: '22px', fontSize: '0.75rem', fontFamily: 'Gilroy-Medium',
                                                backgroundColor: 'transparent',
                                                color: overallServiceCostDifferenceFromApi === 0 ? 'text.primary' : overallServiceCostDifferenceFromApi > 0 ? '#D92D20' : '#039855',
                                                '.MuiChip-label': { px: '4px' }, '.MuiChip-icon': { ml: '0px', mr: '0px', color: 'inherit' },
                                                border: 'none', verticalAlign: 'middle'
                                            }}
                                        />
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "Gilroy-Regular", mt: 0.5 }}>
                                        {`In ${datePeriodText.toLowerCase()}`}
                                    </Typography>
                                </Box>
                            </Paper>
                        )}
                        {/* END OF NEW OVERALL SERVICE COST CARD */}
                        {/* Service Data Table/List Headers: Shown if data is ready */}
                        {selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) && !loadingServices && !errorServices && Object.keys(processedAndGroupedServiceData).length > 0 && dateRange.startDate && (
                            <Box sx={{ display: 'flex', py: 1, borderBottom: 1, borderColor: 'divider', backgroundColor: 'grey.50', pr: { xs:1, sm: 2 }, pl: {xs:1, sm:2}, flexShrink: 0 }}>
                                <Typography variant="caption" sx={{ flex: {xs: '1 1 40%', sm:'0 0 40%'}, fontWeight: 'bold', color: 'text.secondary', fontFamily:'Gilroy-SemiBold', pr:1 }}>SERVICE NAME</Typography>
                                <Typography variant="caption" sx={{ flex: {xs: '1 1 30%', sm:'0 0 25%'}, fontWeight: 'bold', color: 'text.secondary', fontFamily:'Gilroy-SemiBold', textAlign: 'right', pr:1 }}>TOTAL COST ({datePeriodText})</Typography>
                                <Typography variant="caption" sx={{ flex: {xs: '1 1 30%', sm:'0 0 35%'}, fontWeight: 'bold', color: 'text.secondary', fontFamily:'Gilroy-SemiBold', textAlign: 'center' }}>COST TREND ({datePeriodText})</Typography>
                            </Box>
                        )}

                        {/* Scrollable content area for services */}
                        <Box sx={{ overflowY: 'auto', flexGrow: 1, mt: (Object.keys(processedAndGroupedServiceData).length > 0 || loadingServices || errorServices ? 0 : 2) }}>
                            {/* Loading state for services */}
                            {loadingServices && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) && (
                                <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily:"Gilroy-Regular" }}>Loading service data...</Typography>
                                </Stack>
                            )}
                            {/* Error state for services */}
                            {errorServices && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) &&(
                                <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                                    <Alert severity="error" sx={{width: 'auto', fontFamily:"Gilroy-Regular"}}>Error: {errorServices}</Alert>
                                </Stack>
                            )}

                            {/* Display processed service data */}
                            {!loadingServices && !errorServices && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) && dateRange.startDate && (
                                Object.keys(processedAndGroupedServiceData).length > 0 ? (
                                    Object.entries(processedAndGroupedServiceData).map(([clusterName, apps]) => (
                                        // Only render this cluster's services if the cluster is currently selected
                                        selectedClusterNames.includes(clusterName) &&
                                        <Box key={clusterName} sx={{ mb: 0 }}>
                                            {Object.entries(apps).map(([appName, services]) => (
                                                // Only render this app's services if the app is currently selected (or "all apps")
                                                (selectedApplicationNames.length === 0 || selectedApplicationNames.includes(appName)) &&
                                                <Box key={appName} sx={{ mb: 0 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', p:1, pl: {xs:1, sm:2}, backgroundColor: 'grey.100', borderBottom: '1px solid #eee', fontFamily:'Gilroy-Bold' }}>
                                                        {appName} <Typography variant="caption" color="text.secondary" component="span">({clusterName})</Typography> {/* Made span for proper inline display */}
                                                    </Typography>
                                                    {services.length === 0 && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', fontFamily: 'Gilroy-Regular' }}>
                                                            No services match the current filter for this application.
                                                        </Typography>
                                                    )}
                                                    {services.map((service) => ( // Iterate over services in this app
                                                      <CostListItem
                                                          key={service.serviceName}
                                                          name={service.serviceName}
                                                          totalCost={service.totalCost}
                                                          costChange={service.costChange}
                                                          costTrendData={service.costTrendData}
                                                          maxCostInTrend={service.maxCostInTrend}
                                                          dateRange={dateRange}
                                                          onBarMouseEnter={handleBarMouseEnter}
                                                          onBarMouseLeave={handleBarMouseLeave}
                                                      />
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                    ))
                                ) : (
                                     !loadingServices && !errorServices && selectedClusterNames.length > 0 && (selectedApplicationNames.length > 0 || availableApplications.length > 0) && dateRange.startDate && (
                                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 3, fontFamily:"Gilroy-Regular", p:3 }}>
                                            No service data available for the selected filters.
                                        </Typography>
                                    )
                                )
                            )}
                            {/* Message if no clusters/apps are selected (for service section) */}
                            {!loadingServices && !errorServices && (selectedClusterNames.length === 0 || (selectedApplicationNames.length === 0 && availableApplications.length ===0 && !loadingApplications)) && (
                                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 3, fontFamily:"Gilroy-Regular", p:3 }}>
                                    Please select cluster(s) and application(s) to see service costs.
                                </Typography>
                            )}
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', width: { xs: '100%', md: '50%' }, display: 'flex', flexDirection: 'column', maxHeight: '700px' }}>
                <Box sx={{ mb:2, flexShrink: 0 }}>
                    <Typography variant="h6" sx={{fontFamily: "Gilroy-Bold"}}>Add-on Resources Cost Overview</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily:"Gilroy-Regular" }}>
                        {selectedClusterNames.length > 0 ? `Showing for ${datePeriodText}.` : 'Select clusters to view Add on resources costs.'}
                    </Typography>
                </Box>
                <Box sx={{ position: 'relative', height: '100%', width: '100%', flexGrow: 1, minHeight: 250 }}>
                    {loadingAddonCosts && selectedClusterNames.length > 0 && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                            <CircularProgress size={24}/>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily:"Gilroy-Regular" }}>Loading addon data...</Typography>
                        </Stack>
                    )}
                    {errorAddonCosts && selectedClusterNames.length > 0 && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', p:2 }}>
                            <Alert severity="error" sx={{width: 'auto', fontFamily:"Gilroy-Regular"}}>Error: {errorAddonCosts}</Alert>
                        </Stack>
                    )}
                    {/* Ensure chart data is ready and clusters are selected */}
                    {!loadingAddonCosts && !errorAddonCosts && selectedClusterNames.length > 0 && addonChartData.datasets.length > 0 && dateRange.startDate && dateRange.endDate && (
                        <Bar options={addonChartOptions} data={addonChartData} />
                    )}
                    {/* Message when no addon data is available for the selection */}
                    {!loadingAddonCosts && !errorAddonCosts && selectedClusterNames.length > 0 && addonChartData.datasets.length === 0 && !loadingClusters /* Check loadingClusters to avoid showing this msg while parent data is loading */ && dateRange.startDate && dateRange.endDate && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                            <Typography variant="body1" color="text.secondary" sx={{fontFamily:"Gilroy-Regular", p:3, textAlign:'center'}}>
                                No addon cost data available for the selected clusters and timeframe.
                            </Typography>
                        </Stack>
                    )}
                    {/* Message when no clusters are selected */}
                    {!loadingAddonCosts && !errorAddonCosts && selectedClusterNames.length === 0 && !loadingClusters && (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                            <Typography variant="body1" color="text.secondary" sx={{fontFamily:"Gilroy-Regular", p:3, textAlign:'center'}}>
                                Please select clusters to view Add on resources costs.
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Paper>
            </Box> {/* END OF SERVICE COST OVERVIEW SECTION WRAPPER */}

            {/* NEW: AI Agent Cost Overview Section */}
            

                {/* NEW: Notebooks Cost Overview Section */}
                
        </>
    ) : (
        <>
            {/* Non-K8s Cost Overview Section */}
           {/* --- NON-K8S VIEW --- */}
 <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', mb: 3 }}>
   <Typography variant="h6" sx={{fontFamily: "Gilroy-Bold"}}>Cost Overview</Typography>
     <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily:"Gilroy-Regular" }}>
        Showing cost for Non-K8s solutions in {datePeriodText.toLowerCase()}
     </Typography>
      {!loadingNonK8s && (
         <Paper elevation={0} sx={{ py:0.5, px:2, mt: 2, mb: 2, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', borderLeft: '6px solid', borderColor: '#D92D20', backgroundColor: '#FEF3F2', borderRadius: '4px', minWidth: '200px' }}>
             <Typography sx={{ fontFamily: "Gilroy-Regular", display: 'inline-block', mr: 1, fontSize:"14px" }}>Overall Cost:</Typography>
             <Typography sx={{ fontFamily: "Gilroy-Bold", display: 'inline-block', mr: 1, fontSize:"14px" }}>{formatCurrency((overallAiSolutionCost.total + overallDataSolutionCost.total) || 0)}</Typography>
         </Paper>
     )}
     <Box sx={{ position: 'relative', height: 350, width: '100%' }}>
         {loadingNonK8s ? (<Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10 }}><CircularProgress /></Stack>) 
         : (<Line options={clusterChartOptions} data={nonK8sChartData} />)}
     </Box>
 </Paper>

 <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', mb: 3 }}>
     <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }} rowSpacing={{ xs: 1.5, md: 0 }}>
         <Grid item xs={12} lg={6}>
             <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>AI Solutions Cost Overview</Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily: "Gilroy-Regular" }}>{`Showing for ${datePeriodText}.`}</Typography>
         </Grid>
         <Grid item xs={12} lg={6} container justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
             <SolutionToggleBar options={AI_SOLUTION_CATEGORIES} activeOption={activeAiSolution} onToggle={(opt) => setActiveAiSolution(opt as AiSolutionCategory)} />
         </Grid>
     </Grid>
     {!loadingNonK8s && (
         <Paper elevation={0} sx={{ py:0.5, px:2, mb: 2, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', borderLeft: '6px solid', borderColor: overallAiSolutionCost.change >= 0 ? '#D92D20' : '#039855', backgroundColor: overallAiSolutionCost.change >= 0 ? '#FEF3F2' : '#ECFDF3', borderRadius: '4px', minWidth: '200px' }}>
             <Typography sx={{ fontFamily: "Gilroy-Regular", display: 'inline-block', mr: 1, fontSize:"14px" }}>Overall {activeAiSolution} Cost:</Typography>
             <Typography sx={{ fontFamily: "Gilroy-Bold", display: 'inline-block', mr: 1, fontSize:"14px" }}>{formatCurrency(overallAiSolutionCost.total)}</Typography>
         </Paper>
     )}
     {loadingNonK8s ? (<Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}><CircularProgress /></Stack>) 
     : (<>
         <Box sx={{ display: 'flex', py: 1, borderBottom: 1, borderColor: 'divider', mb: 0, backgroundColor: 'grey.50', pr: { xs: 1, sm: 2 }, pl: { xs: 1, sm: 2 } }}>
             <Typography variant="caption" sx={{ flex: { xs: '1 1 40%', sm: '0 0 40%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', pr: 1 }}>SERVICE NAME</Typography>
             <Typography variant="caption" sx={{ flex: { xs: '1 1 30%', sm: '0 0 25%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', textAlign: 'right', pr: 1 }}>TOTAL COST ({datePeriodText})</Typography>
             <Typography variant="caption" sx={{ flex: { xs: '1 1 30%', sm: '0 0 35%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', textAlign: 'center' }}>COST TREND ({datePeriodText})</Typography>
         </Box>
         {(processedAiSolutionsData[activeAiSolution] || []).map(item => ( <CostListItem key={item.name} {...item} dateRange={dateRange} onBarMouseEnter={handleBarMouseEnter} onBarMouseLeave={handleBarMouseLeave}/> ))}
     </>)}
 </Paper>

 <Paper elevation={0} sx={{ p: 2.5, borderRadius: "8px", border: '1px solid', borderColor: 'divider', mb: 3 }}>
     <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }} rowSpacing={{ xs: 1.5, md: 0 }}>
        <Grid item xs={12} lg={6}>
             <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Data Solutions Cost Overview</Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 0, fontFamily: "Gilroy-Regular" }}>{`Showing for ${datePeriodText}.`}</Typography>
         </Grid>
         <Grid item xs={12} lg={6} container justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
            <SolutionToggleBar options={DATA_SOLUTION_CATEGORIES} activeOption={activeDataSolution} onToggle={(opt) => setActiveDataSolution(opt as DataSolutionCategory)} />
         </Grid>
     </Grid>
      {!loadingNonK8s && (
         <Paper elevation={0} sx={{ py:0.5, px:2, mb: 2, display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', borderLeft: '6px solid', borderColor: overallDataSolutionCost.change >= 0 ? '#D92D20' : '#039855', backgroundColor: overallDataSolutionCost.change >= 0 ? '#FEF3F2' : '#ECFDF3', borderRadius: '4px', minWidth: '200px' }}>
             <Typography sx={{ fontFamily: "Gilroy-Regular", display: 'inline-block', mr: 1, fontSize:"14px" }}>Overall {activeDataSolution} Cost:</Typography>
             <Typography sx={{ fontFamily: "Gilroy-Bold", display: 'inline-block', mr: 1, fontSize:"14px" }}>{formatCurrency(overallDataSolutionCost.total)}</Typography>
         </Paper>
     )}
     {loadingNonK8s ? (<Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}><CircularProgress /></Stack>) 
     : (<>
         <Box sx={{ display: 'flex', py: 1, borderBottom: 1, borderColor: 'divider', mb: 0, backgroundColor: 'grey.50', pr: { xs: 1, sm: 2 }, pl: { xs: 1, sm: 2 } }}>
             <Typography variant="caption" sx={{ flex: { xs: '1 1 40%', sm: '0 0 40%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', pr: 1 }}>SERVICE NAME</Typography>
             <Typography variant="caption" sx={{ flex: { xs: '1 1 30%', sm: '0 0 25%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', textAlign: 'right', pr: 1 }}>TOTAL COST ({datePeriodText})</Typography>
             <Typography variant="caption" sx={{ flex: { xs: '1 1 30%', sm: '0 0 35%' }, fontWeight: 'bold', color: 'text.secondary', fontFamily: 'Gilroy-SemiBold', textAlign: 'center' }}>COST TREND ({datePeriodText})</Typography>
         </Box>
         {(processedDataSolutionsData[activeDataSolution] || []).map(item => ( <CostListItem key={item.name} {...item} dateRange={dateRange} onBarMouseEnter={handleBarMouseEnter} onBarMouseLeave={handleBarMouseLeave}/> ))}
     </>)}
 </Paper>
        </>
    )}

    {/* MUI Popper Tooltip for Bar Graphs */}
    {barTooltip.anchorElement && barTooltip.content && (
        <Popper
            open={barTooltip.visible}
            anchorEl={barTooltip.anchorElement}
            placement="top"
            transition
            modifiers={[ { name: 'offset', options: { offset: [0, 8], }, }, ]}
            sx={{ zIndex: 1300 }}
        >
            {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
                <Paper elevation={3} sx={{ p: 1, borderRadius: '4px', bgcolor: '#fff', border: '1px solid #eee', pointerEvents: 'none' }}>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', color: 'grey.700', textAlign:'center', mb: 0.5 }}>
                        On {barTooltip?.content?.date}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Gilroy-Bold', textAlign: 'center', fontSize:'0.875rem' }}>
                        {barTooltip?.content?.cost}
                    </Typography>
                </Paper>
            </Fade>
            )}
        </Popper>
    )}

</Box> {/* End of main content padding box */}
</Box> {/* End of main content box */}
</Container>
);
};
//export default CostDashboard;

const CostDashboard: React.FC = () => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Using your remembered calculation for marginLeft
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {isDrawerActuallyOpen && (
                <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"insights-finops-dashboard"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            )}
            {!isDrawerActuallyOpen && (
                <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"insights"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    // Using your remembered unit and calculation for marginLeft
                    marginLeft: `${currentMarginLeft}vh`,
                    overflowX:'hidden'
                }}
            >
                <CostDashboardContent />
            </Box>
        </Box>
    );
};

export default CostDashboard;







